import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Plus, Trash2, Box, Save, ArrowLeft, Loader2, Calculator, ChevronDown, ChevronUp,
  Printer, ChevronsUpDown, Check, Search, UserPlus, Building2, MapPin
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

// ─── Constants & Charge Descriptions ──────────────────────────────────────────
const CURRENCY_OPTIONS = [
  'INR',
  'USD',
  'EUR',
  'GBP',
  'AUD',
  'AED',
  'SGD',
  'CNY',
  'JPY',
  'CAD',
  'CHF',
  'HKD',
  'SAR',
  'MYR',
  'THB',
  'KWD',
  'OMR',
  'QAR',
  'BHD',
  'NZD',
];

const EXW_CHARGE_OPTIONS = [
  'Transport Pickup Charges',
  'CFS Charges',
  'Custom Clearance Charges',
  'EDI Charges',
  'CMC Charges',
  'GACC / ADANI Charges',
  'ADC / NOC Charges',
  'Documentation Charges',
  'Loading / Unloading Charges',
  'FASSI Charges',
  'Survey Charges',
  'Laching Choking Charges',
  'Fumigating Charges',
  'Certificate Origin Charges',
  'Miscellaneous - Others',
];

const AIR_FREIGHT_CHARGE_OPTIONS = [
  'Air Freight Charges',
  'FSC Charges',
  'XRAY Charges',
  'MCC Charges',
  'AMS Charges',
  'CGC Charges',
  'AIRWAY Bill Charges',
  'ASI Charges',
  'MAWB Charges',
  'HAWB Charges',
  'AWC Charges',
  'DTC Charges',
  'GMAX Charges',
  'OKTOFORWARD Charges',
  'Escort Charges',
  'Miscellaneous - Others',
];

const DESTINATION_CHARGE_OPTIONS = [
  'Transport Deliver Charges',
  'CFS Charges',
  'Custom Clearance Charges',
  'EDI Charges',
  'CMC Charges',
  'GACC / ADANI Charges',
  'ADC / NOC Charges',
  'Documentation Charges',
  'Loading / Unloading Charges',
  'FASSI Charges',
  'Survey Charges',
  'Laching Choking Charges',
  'Fumigating Charges',
  'Certificate Origin Charges',
  'Miscellaneous - Others',
];

const INCOTERM_OPTIONS = ['EXW', 'CIF', 'FOB', 'DAP', 'DDU', 'DDP', 'CNF', 'CNI', 'OTHERS'];
const DOMESTIC_TERM_OPTIONS = ['PAID', 'FOD', 'TBB', 'OTHERS'];

// ─── Dimension Calculator Helpers (Exact AIR Docket implementation) ────────────
const PACKAGE_TYPE_OPTIONS = ['Boxes', 'Pallets', 'Drums', 'Cartons', 'Crates', 'Wooden Cases', 'Bags', 'Rolls', 'Loose'];

const EMPTY_DIMENSION = {
  id: '',
  package_type: 'Boxes',
  packages: '',
  no_of_packages: '',
  gross_weight: '',
  length: '',
  width: '',
  height: '',
  unit: 'CM',
  cbm: '',
  vol_weight: '',
};

const CBM_FACTORS = {
  CM: 1 / 1_000_000,
  M:  1,
  MM: 1 / 1_000_000_000,
  IN: 0.000016387064,
  FT: 0.028316846592,
};

const DIMENSION_UNITS = [
  { value: 'CM', label: 'Centimeters (CM)' },
  { value: 'M',  label: 'Meters (M)' },
  { value: 'MM', label: 'Millimeters (MM)' },
  { value: 'IN', label: 'Inches (IN)' },
  { value: 'FT', label: 'Feet (FT)' },
];

const calcCBM = (l, w, h, qty, unit) => {
  const factor = CBM_FACTORS[unit] || CBM_FACTORS.CM;
  return (l && w && h && qty) ? (l * w * h * qty * factor).toFixed(4) : '';
};

const TO_CM3 = { CM: 1, M: 1_000_000, MM: 0.001, IN: 16.387064, FT: 28316.846592 };
const calcVolWeight = (l, w, h, qty, unit, divisor) => {
  if (!l || !w || !h || !qty) return '';
  const cm3 = TO_CM3[unit] || 1;
  return (l * w * h * cm3 * qty / divisor).toFixed(2);
};

// ─── Schema Engine ────────────────────────────────────────────────────────────
const SCHEMAS = {
  AIR: {
    origin_label: 'Pick Up / Origin Address',
    loading_label: 'AOL – Airport of Loading',
    discharge_label: 'AOD – Airport of Discharge',
    dest_label: 'Delivery At / Destination',
    defaultGroups: [
      { group_label: 'A', group_name: 'EXW / Origin Charges', sort_order: 0, hasAirline: false, hasTariff: false },
      { group_label: 'B', group_name: 'Air Freight',           sort_order: 1, hasAirline: true,  hasTariff: true  },
      { group_label: 'C', group_name: 'Destination Charges',   sort_order: 2, hasAirline: false, hasTariff: false },
    ],
  },
  SEA: {
    origin_label: 'Pick Up / Origin Address',
    loading_label: 'POL – Port of Loading',
    discharge_label: 'POD – Port of Discharge',
    dest_label: 'Delivery At / Destination',
    defaultGroups: [
      { group_label: 'A', group_name: 'Origin Charges', sort_order: 0, hasAirline: false, hasTariff: false },
      { group_label: 'B', group_name: 'Ocean Freight',  sort_order: 1, hasAirline: true,  hasTariff: true  },
      { group_label: 'C', group_name: 'Destination Charges', sort_order: 2, hasAirline: false, hasTariff: false },
    ],
  },
  ROAD: {
    origin_label: 'From (Origin)',
    loading_label: '',
    discharge_label: '',
    dest_label: 'To (Destination)',
    defaultGroups: [
      { group_label: 'A', group_name: 'Freight Charges',     sort_order: 0, hasAirline: false, hasTariff: false },
      { group_label: 'B', group_name: 'Additional Charges',  sort_order: 1, hasAirline: false, hasTariff: false },
    ],
  },
};
const getSchema = (mode) => SCHEMAS[mode] || SCHEMAS.AIR;

// ─── General Options ──────────────────────────────────────────────────────────
const SCOPE_OPTIONS = ['INTERNATIONAL', 'DOMESTIC'];
const MODE_OPTIONS  = ['AIR', 'SEA', 'ROAD', 'RAIL', 'COURIER'];
const DIR_OPTIONS   = ['INWARD', 'OUTWARD'];
const STATUS_OPTIONS = ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED'];

const DOMESTIC_SERVICE_OPTIONS = ['General', 'Express', 'Refer'];

const SERVICE_OPTIONS = [
  'Door to Door', 'Airport to Airport', 'Door to Airport', 'Airport to Door',
  'Port to Port', 'Port to Door', 'Door to Port', 'Customs Clearance Only',
];
const CARGO_OPTIONS = [
  'General', 'Dangerous Goods', 'Temperature Controlled', 'Hazardous',
  'Non-Hazardous', 'Stackable', 'Non-Stackable', 'Perishable', 'Fragile',
];
const CONTAINER_TYPE_OPTIONS = ["20'GP", "40'GP", "40'HC", "45'HC", "20'RF", "40'RF", "20'OT", "40'OT"];
const emptyContainer = () => ({ _key: crypto.randomUUID(), container_type: '', general_count: '', reefer_count: '' });

// ─── Domestic Logistics Quotation Presets (Matching Screenshot) ───────────────
const LOGISTICS_RATE_TYPES = [
  'Per Trip',
  'Included',
  'Lump-sum',
  'At Actuals',
  'Per Day',
  'Per KG',
  'Per Ton',
  'Per Box'
];

const DEFAULT_LOGISTICS_CHARGES = [
  { _key: 'leg-1', description: 'Basic Freight Charges (Fixed Route Route-Trip)', rate_type: 'Per Trip', amount: '45000.00' },
  { _key: 'leg-2', description: 'Fuel Surcharge (Adjusted based on current market rates)', rate_type: 'Included', amount: '0.00' },
  { _key: 'leg-3', description: 'Loading & Unloading Charges (At origin and destination)', rate_type: 'Lump-sum', amount: '4500.00' },
  { _key: 'leg-4', description: 'Toll Taxes, State Permits & Border Clearance', rate_type: 'At Actuals', amount: '3200.00' },
  { _key: 'leg-5', description: 'Detention Charges (Applicable after 24 hrs free loading time)', rate_type: 'Per Day', amount: '2500.00' },
];

const LOGISTICS_CHARGE_PRESETS = [
  'Basic Freight Charges (Fixed Route Route-Trip)',
  'Fuel Surcharge (Adjusted based on current market rates)',
  'Loading & Unloading Charges (At origin and destination)',
  'Toll Taxes, State Permits & Border Clearance',
  'Detention Charges (Applicable after 24 hrs free loading time)',
  'Door Pickup / Collection Charges',
  'Door Delivery Charges',
  'Multi-point Loading / Unloading Charges',
  'Green Tax / State Entry Tax',
  'FOV / Cargo Transit Insurance Surcharge',
  'Escort Charges',
  'Miscellaneous Charges',
];

const DEFAULT_LOGISTICS_REMARKS = [
  { _key: 'rem-1', text: 'Detention Charges: Applicable after 24 hrs free loading/unloading time.' },
  { _key: 'rem-2', text: 'Toll Taxes, State Permits & Border Clearance: At actuals against receipts (unless included in freight).' },
  { _key: 'rem-3', text: 'Payment Terms: 80% Advance upon vehicle placement / dispatch, 20% on delivery and physical POD submission.' },
  { _key: 'rem-4', text: 'Quotation Validity: 7 Days from date of issue.' },
];


const VEHICLE_TYPE_OPTIONS = ['LCV', 'MCV', 'HCV', 'TRAILER', '2-WHEELER', '3-WHEELER', 'OPEN BODY'];
const VEHICLE_SIZE_OPTIONS = ['407 (1T)', 'TATA ACE (0.5T)', '17ft (5T)', '19ft (7T)', '22ft (9T)', '24ft (12T)', '32ft SXL (15T)', '32ft MXL (18T)', '40ft Trailer', '20ft Container', '40ft Container'];
const WEIGHT_SLAB_OPTIONS = ['0-50 KGS', '51-100 KGS', '101-250 KGS', '251-500 KGS', '501-1000 KGS', '1001-2000 KGS', '2001-5000 KGS', '5001-10000 KGS', '10001+ KGS'];
const ZONE_OPTIONS = ['Zone A', 'Zone B', 'Zone C', 'Zone D', 'Zone E', 'North India', 'South India', 'East India', 'West India', 'Pan India'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const emptyItem = (extra = {}) => ({ _key: crypto.randomUUID(), description: '', airline_vendor: '', tariff: '', amount: '', qty: '1', qty_unit: '', total: '', ...extra });
const emptyRemark = () => ({ _key: crypto.randomUUID(), text: '' });

const calcItemTotal = (amt, qty) => {
  const a = parseFloat(amt) || 0;
  const q = parseFloat(qty) || 0;
  return a && q ? (a * q).toFixed(2) : '';
};

const calcGroupTotal = (items) =>
  items.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

// ─── Sub-components ───────────────────────────────────────────────────────────
const FormLabel = ({ children, required }) => (
  <Label className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1 block">
    {children}{required && <span className="text-accent-red ml-1">*</span>}
  </Label>
);

const SectionCard = ({ title, children, icon: Icon, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-white border border-gray-100 shadow-sm">
      <div
        className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-accent-blue" />}
          <h3 className="text-base font-semibold text-text-dark font-manrope">{title}</h3>
        </div>
        {collapsible && (open ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />)}
      </div>
      {open && <div className="p-6">{children}</div>}
    </Card>
  );
};

// ─── Searchable Combobox for Master Selection (Locations, Airports, Ports) ─────
const SearchableCombobox = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  labelField = "name",
  subField = "subtext",
  allowCustom = true,
  testId
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const s = search.toLowerCase();
    return options.filter(opt => {
      const lbl = (typeof opt === 'string' ? opt : (opt[labelField] || opt.name || opt.label || '')).toLowerCase();
      const sub = (typeof opt === 'object' && opt[subField] ? opt[subField] : '').toLowerCase();
      return lbl.includes(s) || sub.includes(s);
    });
  }, [options, search, labelField, subField]);

  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    const match = options.find(opt => (typeof opt === 'string' ? opt : opt.id || opt.value || opt[labelField]) === value);
    if (match) return typeof match === 'string' ? match : (match[labelField] || match.name || match.label);
    return value;
  }, [value, options, labelField]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full justify-between border-gray-200 font-ibm font-normal text-sm h-9 hover:bg-gray-50 px-3 bg-white"
        >
          <span className="truncate text-left font-ibm text-text-dark">
            {selectedDisplay || <span className="text-gray-400">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-2 bg-white border border-gray-200 shadow-lg rounded-md" align="start">
        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Type to search..."
            className="pl-8 h-8 text-xs font-ibm"
            autoFocus
          />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {allowCustom && search.trim() && !options.some(opt => (typeof opt === 'string' ? opt : opt[labelField]).toLowerCase() === search.trim().toLowerCase()) && (
            <button
              type="button"
              onClick={() => {
                onChange(search.trim());
                setOpen(false);
                setSearch('');
              }}
              className="w-full text-left px-2 py-1.5 text-xs font-ibm rounded hover:bg-blue-50 text-accent-blue font-medium flex items-center justify-between"
            >
              <span>Use &quot;{search.trim()}&quot;</span>
              <span className="text-[10px] text-gray-400">custom value</span>
            </button>
          )}

          {filtered.length === 0 && !search.trim() && (
            <div className="p-3 text-center text-xs text-gray-400 font-ibm">No options available</div>
          )}

          {filtered.map((opt, i) => {
            const optVal = typeof opt === 'string' ? opt : (opt.value || opt[labelField] || opt.id);
            const optLbl = typeof opt === 'string' ? opt : (opt[labelField] || opt.name || opt.label);
            const optSub = typeof opt === 'object' ? opt[subField] : null;
            const isSelected = value === optVal || value === optLbl;

            return (
              <button
                key={opt.id || optVal || i}
                type="button"
                onClick={() => {
                  onChange(optLbl);
                  setOpen(false);
                  setSearch('');
                }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs font-ibm flex items-center justify-between transition-colors ${
                  isSelected ? 'bg-accent-blue/10 text-accent-blue font-semibold' : 'hover:bg-gray-50 text-text-dark'
                }`}
              >
                <div className="truncate mr-2">
                  <div className="truncate">{optLbl}</div>
                  {optSub && <div className="text-[10px] text-gray-400 truncate">{optSub}</div>}
                </div>
                {isSelected && <Check className="w-3.5 h-3.5 text-accent-blue flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ─── Inline Editable Charge Items Table with Preset Description Dropdowns ──────
const ChargeGroupTable = ({ group, groupIdx, schema, onChange }) => {
  const items = group.items || [];
  const hasAirline = schema?.hasAirline ?? false;
  const hasTariff  = schema?.hasTariff  ?? false;

  // Determine standard description options based on group label
  const descriptionOptions = useMemo(() => {
    const lbl = (group.group_label || '').toUpperCase();
    if (lbl === 'A') return EXW_CHARGE_OPTIONS;
    if (lbl === 'B') return AIR_FREIGHT_CHARGE_OPTIONS;
    if (lbl === 'C') return DESTINATION_CHARGE_OPTIONS;
    return EXW_CHARGE_OPTIONS;
  }, [group.group_label]);

  const miscLabel = 'Miscellaneous - Others';

  const updateItem = (idx, field, value) => {
    const updated = items.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      if (field === 'amount' || field === 'qty') {
        next.total = calcItemTotal(field === 'amount' ? value : item.amount, field === 'qty' ? value : item.qty);
      }
      return next;
    });
    onChange({ ...group, items: updated, group_total: calcGroupTotal(updated) });
  };

  const addItem = () => onChange({ ...group, items: [...items, emptyItem()] });
  const removeItem = (idx) => {
    const updated = items.filter((_, i) => i !== idx);
    onChange({ ...group, items: updated, group_total: calcGroupTotal(updated) });
  };

  const groupTotal = group.group_total || calcGroupTotal(items);

  return (
    <div className="mb-6 last:mb-0">
      {/* Group Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-accent-blue text-white text-xs font-bold flex items-center justify-center font-manrope">
            {group.group_label}
          </span>
          <Input
            value={group.group_name}
            onChange={e => onChange({ ...group, group_name: e.target.value })}
            className="text-sm font-semibold text-text-dark font-ibm w-56 border-0 border-b border-gray-200 rounded-none focus:border-accent-blue bg-transparent px-1"
            placeholder="Group name"
            data-testid={`group-name-${groupIdx}`}
          />
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addItem} className="font-ibm text-xs" data-testid={`add-item-${groupIdx}`}>
          <Plus className="w-3 h-3 mr-1" /> Add Row
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm w-64">Description</th>
              {hasAirline && <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Airline / Vendor</th>}
              {hasTariff  && <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Tariff</th>}
              <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Amount</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Qty</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Unit</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary font-ibm">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.length === 0 && (
              <tr>
                <td colSpan={7 + (hasAirline ? 1 : 0) + (hasTariff ? 1 : 0)} className="px-3 py-4 text-center text-xs text-text-secondary font-ibm">
                  No items — click Add Row
                </td>
              </tr>
            )}
            {items.map((item, idx) => {
              const currentDesc = (item.description || '').trim();
              const matchedStandard = descriptionOptions.find(
                opt => opt.toLowerCase() === currentDesc.toLowerCase() &&
                       opt !== 'Miscellaneous - Others' &&
                       opt !== 'Miscellaneous Charges'
              );
              const isStandard = Boolean(matchedStandard);
              const isMisc = currentDesc === 'Miscellaneous - Others' ||
                             currentDesc === 'Miscellaneous Charges' ||
                             (!isStandard && currentDesc !== '');
              const selectValue = isStandard ? matchedStandard : (isMisc ? 'Miscellaneous - Others' : '__none__');

              return (
                <tr key={item._key || item.id || idx} className="hover:bg-gray-50/60">
                  <td className="px-2 py-1.5 w-72">
                    <div className="space-y-1">
                      <select
                        value={selectValue}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === '__none__') {
                            updateItem(idx, 'description', '');
                          } else if (val === 'Miscellaneous - Others' || val === 'Miscellaneous Charges') {
                            updateItem(idx, 'description', 'Miscellaneous - Others');
                          } else {
                            updateItem(idx, 'description', val);
                          }
                        }}
                        className="w-full h-8 text-xs font-ibm border border-gray-200 rounded px-2 bg-white text-text-dark focus:outline-none focus:border-accent-blue"
                        data-testid={`item-desc-select-${groupIdx}-${idx}`}
                      >
                        <option value="__none__">-- Select Charge --</option>
                        {descriptionOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>

                      {/* When Miscellaneous or custom entered, allow typing custom description */}
                      {isMisc && (
                        <Input
                          value={item.description || ''}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder="Type custom charge description..."
                          className="h-7 text-xs font-ibm bg-amber-50/40 border-amber-200 focus:border-amber-400 px-2"
                          data-testid={`item-desc-${groupIdx}-${idx}`}
                        />
                      )}
                    </div>
                  </td>
                  {hasAirline && (
                    <td className="px-2 py-1.5">
                      <Input
                        value={item.airline_vendor || ''}
                        onChange={e => updateItem(idx, 'airline_vendor', e.target.value)}
                        className="border-0 bg-transparent text-sm font-ibm h-8 px-1 focus:ring-0"
                        placeholder="Airline / Carrier"
                        data-testid={`item-airline-${groupIdx}-${idx}`}
                      />
                    </td>
                  )}
                  {hasTariff && (
                    <td className="px-2 py-1.5">
                      <Input
                        value={item.tariff || ''}
                        onChange={e => updateItem(idx, 'tariff', e.target.value)}
                        className="border-0 bg-transparent text-sm font-ibm h-8 px-1 focus:ring-0"
                        placeholder="GCR..."
                        data-testid={`item-tariff-${groupIdx}-${idx}`}
                      />
                    </td>
                  )}
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      step="0.01"
                      value={(item.amount !== undefined && item.amount !== null) ? item.amount : ''}
                      onChange={e => updateItem(idx, 'amount', e.target.value)}
                      className="border-0 bg-transparent text-sm font-ibm h-8 px-1 focus:ring-0 w-28"
                      placeholder="0.00"
                      data-testid={`item-amount-${groupIdx}-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      step="0.01"
                      value={(item.qty !== undefined && item.qty !== null) ? item.qty : ''}
                      onChange={e => updateItem(idx, 'qty', e.target.value)}
                      className="border-0 bg-transparent text-sm font-ibm h-8 px-1 focus:ring-0 w-20"
                      placeholder="1"
                      data-testid={`item-qty-${groupIdx}-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={item.qty_unit || ''}
                      onChange={e => updateItem(idx, 'qty_unit', e.target.value)}
                      className="border-0 bg-transparent text-sm font-ibm h-8 px-1 focus:ring-0 w-16"
                      placeholder="KGS"
                      data-testid={`item-unit-${groupIdx}-${idx}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <span className="text-sm font-semibold text-text-dark font-ibm">
                      {item.total ? parseFloat(item.total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-6 w-6 p-0" data-testid={`remove-item-${groupIdx}-${idx}`}>
                      <Trash2 className="w-3 h-3 text-accent-red" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {items.length > 0 && (
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td colSpan={5 + (hasAirline ? 1 : 0) + (hasTariff ? 1 : 0)} className="px-3 py-2 text-right text-xs font-bold font-ibm text-text-dark uppercase">
                  Total {group.group_name} ({group.group_label})
                </td>
                <td className="px-3 py-2 text-right text-sm font-bold text-text-dark font-manrope" data-testid={`group-total-${groupIdx}`}>
                  {groupTotal ? groupTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─── Main Form Component ──────────────────────────────────────────────────────
export default function EstimateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    estimate_no: '',
    estimate_date: today,
    customer_id: '',
    customer_branch_id: '',
    is_new_customer: false,
    new_customer_data: null,
    party_name: '',
    place: '',
    attention: '',
    intro_remarks: 'With ref. to the subject please note below quote.',
    subject: '',
    // Shipment classifiers
    shipment_scope: 'INTERNATIONAL',
    mode_of_transport: 'AIR',
    movement_direction: 'INWARD',
    service: '',
    cargo_type: '',
    // Movement
    pickup_address: '',
    aol: '',
    aod: '',
    delivery_at: '',
    term: 'EXW',
    commodity: '',
    po_number: '',
    shipment_notes: '',
    // Summary
    total_amount: 0,
    currency: 'INR',
    // Terms
    highlight_notes: '',
    payment_terms: '',
    validity_notes: '',
    standard_conditions: '',
    signature_text: 'Thanking you in advance.\n\nFor, Stavan International LLP',
    status: 'DRAFT',
    sea_load_type: 'LCL',
    sea_gross_weight: '',
    road_load_type: 'FTL',
    road_from: '',
    road_to: '',
    road_location: '',
    road_zone: '',
    road_vehicle_type: '',
    road_vehicle_size: '',
    road_weight_slab: '',
    road_material_type: '',
    road_rate_per_kg: '',
    road_tat: '',
  });

  // TAT (Transit Time) with Days / Hours unit support
  const parseTat = (raw) => {
    if (!raw || !String(raw).trim()) return { val: '', unit: 'Hours' };
    const str = String(raw).trim();
    const isDays = /days?/i.test(str);
    const isHours = /hours?|hrs?/i.test(str);
    const cleanVal = str.replace(/hours?|hrs?|days?/gi, '').trim();
    if (isDays) return { val: cleanVal, unit: 'Days' };
    if (isHours) return { val: cleanVal, unit: 'Hours' };
    const n = parseFloat(cleanVal);
    if (!isNaN(n) && n < 12) return { val: cleanVal, unit: 'Days' };
    return { val: cleanVal, unit: 'Hours' };
  };

  const [tatValue, setTatValue] = useState('');
  const [tatUnit, setTatUnit] = useState('Hours');

  const handleTatChange = (val, unit) => {
    setTatValue(val);
    setTatUnit(unit);
    const trimmed = val ? String(val).trim() : '';
    const finalTat = trimmed ? `${trimmed} ${unit}` : '';
    setForm(prev => ({ ...prev, road_tat: finalTat }));
  };

  // Masters State
  const [customers, setCustomers] = useState([]);
  const [customerBranches, setCustomerBranches] = useState([]);
  const [companyBranches, setCompanyBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [masterVehicleTypes, setMasterVehicleTypes] = useState([]);
  const [isCustomerPopoverOpen, setIsCustomerPopoverOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  // Add New Customer State
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    business_name: '',
    contact_person: '',
    designation: 'Manager',
    phone: '',
    email: '',
    branch_nick_name: 'Head Office',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    gst_number: '',
    customer_type: 'Company',
    remarks: '',
  });

  // Incoterm custom value helper
  const [customTerm, setCustomTerm] = useState('');

  // ── Dimension Calculator State ───────────────────────────────────────────────
  const [dimensionUnit, setDimensionUnit] = useState('CM');
  const [specialCaseCharges, setSpecialCaseCharges] = useState(false);
  const [airlineActualWeight, setAirlineActualWeight] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [dimensions, setDimensions] = useState([{ ...EMPTY_DIMENSION }]);

  // Legacy packages state (kept in sync for backward compatibility)
  const [packages, setPackages] = useState([]);
  const [containers, setContainers] = useState([emptyContainer()]);
  const [chargeGroups, setChargeGroups] = useState([]);

  // ── Domestic Logistics Commercial Quotation State ───────────────────────────
  const [logisticsItems, setLogisticsItems] = useState(
    DEFAULT_LOGISTICS_CHARGES.map(i => ({ ...i, _key: crypto.randomUUID() }))
  );
  const [logisticsGstRate, setLogisticsGstRate] = useState('5');
  const [logisticsGstLabel, setLogisticsGstLabel] = useState('GST (5% GTA RCM/FCM)');

  // Super admin support
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const [remarks, setRemarks] = useState([
    { _key: '1', text: 'Subject to ahmedabad jurisdiction' },
    { _key: '2', text: 'GST/ Taxes as per Govt. rules and regulation.' },
    { _key: '3', text: 'Payment term : 45 days' },
    { _key: '4', text: 'Any miscellaneous expenses will be on prior approval only.' },
    { _key: '5', text: 'Duty will be paid by consignee only.' },
    { _key: '6', text: 'This is computer Generated quote that do not require stamp and sign.' },
  ]);

  // Load masters on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'super_admin') {
        setIsSuperAdmin(true);
      }
      if (payload.company_id) {
        setSelectedCompanyId(payload.company_id);
      }
      // Fetch companies with allowed_shipment_types for all users
      fetch(`${API}/api/companies`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(comps => {
          setCompanies(comps);
          const activeCmpId = payload.company_id || (comps.length > 0 ? comps[0].id : '');
          if (activeCmpId) {
            setSelectedCompanyId(activeCmpId);
            if (!isEdit) {
              const activeCmp = comps.find(c => c.id === activeCmpId);
              if (activeCmp) {
                setForm(p => ({
                  ...p,
                  company_id: activeCmpId,
                  signature_text: `Thanking you in advance.\n\nFor, ${activeCmp.name}`,
                }));
              }
            }
          }
        })
        .catch(() => {});
    } catch { /* parse error */ }

    // Fetch customers
    fetch(`${API}/api/customers?limit=500`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const list = d.items || d || [];
        setCustomers(list);
      })
      .catch(() => {});

    // Fetch locations from master (enhanced with city & area label)
    fetch(`${API}/api/locations?limit=500`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const raw = d.items || d || [];
        setLocations(raw.map(l => {
          const locName = (l.location_name || l.name || '').trim();
          const cityName = (l.city || '').trim();
          let display = locName;
          if (cityName && locName && cityName.toLowerCase() !== locName.toLowerCase()) {
            display = `${cityName} - ${locName}`;
          } else if (cityName && !locName) {
            display = cityName;
          }
          if (l.location_code) {
            display += ` (${l.location_code})`;
          }
          return {
            ...l,
            displayName: display || 'Unnamed Location',
            subtext: [l.city, l.state, l.country].filter(Boolean).join(', ')
          };
        }));
      })
      .catch(() => {});

    // Fetch vehicle types and sizes from master
    fetch(`${API}/api/vehicle-types?limit=500`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const list = Array.isArray(d) ? d : (d.items || []);
        setMasterVehicleTypes(list);
      })
      .catch(() => {});

    // Fetch company branches as fallback
    fetch(`${API}/api/branches`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCompanyBranches(d || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch branches for selected customer
  const fetchCustomerBranches = useCallback(async (custId, selectBranchId = null) => {
    if (!custId) {
      setCustomerBranches([]);
      return;
    }
    try {
      const res = await fetch(`${API}/api/customers/${custId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const bList = data.branches || [];
        setCustomerBranches(bList);
        if (bList.length > 0) {
          const target = selectBranchId ? bList.find(b => b.id === selectBranchId) : (bList.find(b => b.is_head_office) || bList[0]);
          if (target && !selectBranchId) {
            setForm(prev => ({
              ...prev,
              customer_branch_id: target.id,
              place: prev.place || target.city || '',
              attention: prev.attention || target.contact_person || '',
            }));
          }
        }
      }
    } catch (e) {
      console.error('Error fetching customer branches:', e);
    }
  }, []);

  // Initialize charge groups based on mode
  const initChargeGroups = useCallback((mode) => {
    const schema = getSchema(mode);
    return schema.defaultGroups.map(g => ({
      ...g, id: undefined, group_total: 0, items: [],
      _key: crypto.randomUUID(),
    }));
  }, []);

  // Fetch next estimate number on create
  useEffect(() => {
    if (!isEdit) {
      const params = new URLSearchParams();
      if (selectedCompanyId) params.append('company_id', selectedCompanyId);
      fetch(`${API}/api/estimates/next-number?${params}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setForm(f => ({ ...f, estimate_no: d.estimate_no })))
        .catch(() => {});
      setChargeGroups(initChargeGroups('AIR'));
    }
  }, [isEdit, initChargeGroups, selectedCompanyId]);

  // Load estimate for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    fetch(`${API}/api/estimates/${id}`, { headers: authHeaders() })
      .then(r => {
        if (!r.ok) {
          throw new Error(`Estimate not found (HTTP ${r.status})`);
        }
        return r.json();
      })
      .then(data => {
        const isNewCust = Boolean(data.is_new_customer);
        setIsNewCustomer(isNewCust);
        if (isNewCust && data.new_customer_data) {
          try {
            const parsed = typeof data.new_customer_data === 'string' ? JSON.parse(data.new_customer_data) : data.new_customer_data;
            setNewCustomerData(parsed);
          } catch {}
        }

        // Incoterm / Freight Term setup
        const isDom = (data.shipment_scope === 'DOMESTIC');
        const allowedTerms = isDom ? DOMESTIC_TERM_OPTIONS : INCOTERM_OPTIONS;
        const defaultT = isDom ? 'PAID' : 'EXW';
        const termVal = data.term || defaultT;
        if (allowedTerms.includes(termVal) && termVal !== 'OTHERS') {
          setCustomTerm('');
        } else {
          setCustomTerm(termVal === 'OTHERS' ? '' : termVal);
        }

        setForm({
          estimate_no: data.estimate_no || '',
          estimate_date: data.estimate_date ? String(data.estimate_date).split('T')[0] : today,
          customer_id: data.customer_id || '',
          customer_branch_id: data.customer_branch_id || '',
          is_new_customer: isNewCust,
          new_customer_data: data.new_customer_data || null,
          party_name: data.party_name || '',
          place: data.place || '',
          attention: data.attention || '',
          intro_remarks: data.intro_remarks || 'With ref. to the subject please note below quote.',
          subject: data.subject || '',
          shipment_scope: data.shipment_scope || 'INTERNATIONAL',
          mode_of_transport: data.mode_of_transport || 'AIR',
          movement_direction: data.movement_direction || 'INWARD',
          service: data.service || ((data.shipment_scope === 'DOMESTIC' || data.mode_of_transport === 'ROAD' || data.mode_of_transport === 'RAIL') ? 'General' : ''),
          cargo_type: data.cargo_type || '',
          pickup_address: data.pickup_address || '',
          aol: data.aol || '',
          aod: data.aod || '',
          delivery_at: data.delivery_at || '',
          term: termVal,
          commodity: data.commodity || '',
          po_number: data.po_number || '',
          shipment_notes: data.shipment_notes || '',
          total_amount: data.total_amount || 0,
          currency: data.currency || 'INR',
          highlight_notes: data.highlight_notes || '',
          payment_terms: data.payment_terms || '',
          validity_notes: data.validity_notes || '',
          standard_conditions: data.standard_conditions || '',
          signature_text: data.signature_text || 'Thanking you in advance.\n\nFor, Stavan International LLP\n\n\n(Authorized Signatory)',
          status: data.status || 'DRAFT',
          sea_load_type: data.sea_load_type || 'LCL',
          sea_gross_weight: data.sea_gross_weight || '',
          road_load_type: data.road_load_type || 'FTL',
          road_from: data.road_from || '',
          road_to: data.road_to || '',
          road_location: data.road_location || '',
          road_zone: data.road_zone || '',
          road_vehicle_type: data.road_vehicle_type || '',
          road_vehicle_size: data.road_vehicle_size || '',
          road_weight_slab: data.road_weight_slab || '',
          road_material_type: data.road_material_type || '',
          road_rate_per_kg: data.road_rate_per_kg || '',
          road_tat: data.road_tat || '',
        });

        const parsedTat = parseTat(data.road_tat);
        setTatValue(parsedTat.val);
        setTatUnit(parsedTat.unit);

        // Load branches if customer_id exists
        if (data.customer_id) {
          fetchCustomerBranches(data.customer_id, data.customer_branch_id);
        }

        // Dimension calculator state restoration
        const loadedUnit = data.dimension_unit || data.packages?.[0]?.dimension_unit || 'CM';
        setDimensionUnit(loadedUnit);
        setSpecialCaseCharges(Boolean(data.special_case_charges));
        setAirlineActualWeight(data.airline_actual_weight ? String(data.airline_actual_weight) : '');
        
        const effectiveGross = (data.gross_weight !== null && data.gross_weight !== undefined && data.gross_weight !== 0)
          ? String(data.gross_weight)
          : (data.packages?.[0]?.gross_weight ? String(data.packages[0].gross_weight) : '');
        setGrossWeight(effectiveGross);

        let dimsLoaded = false;
        if (data.dimensions_json) {
          try {
            const dims = typeof data.dimensions_json === 'string' ? JSON.parse(data.dimensions_json) : data.dimensions_json;
            if (Array.isArray(dims) && dims.length > 0) {
              const div = data.shipment_scope === 'DOMESTIC' ? 5000 : 6000;
              const isAir = (data.mode_of_transport || 'AIR') === 'AIR';
              const filled = dims.map((row, i) => {
                const l = parseFloat(row.length) || 0;
                const w = parseFloat(row.width) || 0;
                const h = parseFloat(row.height) || 0;
                const q = parseFloat(row.packages || row.no_of_packages) || 0;
                const rUnit = row.unit || loadedUnit;
                const normUnit = (rUnit === 'Inches' || rUnit === 'IN') ? 'IN' : 'CM';
                return {
                  ...row,
                  id: row.id || row._key || crypto.randomUUID(),
                  package_type: row.package_type || 'Boxes',
                  packages: String(row.packages || row.no_of_packages || ''),
                  no_of_packages: String(row.no_of_packages || row.packages || ''),
                  gross_weight: row.gross_weight !== undefined && row.gross_weight !== null ? String(row.gross_weight) : '',
                  length: row.length !== undefined && row.length !== null ? String(row.length) : '',
                  width: row.width !== undefined && row.width !== null ? String(row.width) : '',
                  height: row.height !== undefined && row.height !== null ? String(row.height) : '',
                  unit: (rUnit === 'Inches' || rUnit === 'IN') ? 'Inches' : 'CM',
                  cbm: row.cbm || calcCBM(l, w, h, q, normUnit),
                  vol_weight: row.vol_weight || (isAir ? calcVolWeight(l, w, h, q, normUnit, div) : ''),
                };
              });
              setDimensions(filled);
              dimsLoaded = true;
            }
          } catch {}
        }
        
        if (!dimsLoaded && data.packages?.length > 0) {
          // Fallback from legacy packages
          const dims = data.packages.map(p => {
            const parts = (p.dimension || '').split('x');
            const l = parseFloat(parts[0]) || 0;
            const w = parseFloat(parts[1]) || 0;
            const h = parseFloat(parts[2]) || 0;
            const q = parseFloat(p.no_of_packages) || 1;
            const u = p.dimension_unit || loadedUnit;
            const div = data.shipment_scope === 'DOMESTIC' ? 5000 : 6000;
            return {
              length: parts[0] || '',
              width: parts[1] || '',
              height: parts[2] || '',
              no_of_packages: String(p.no_of_packages || '1'),
              cbm: calcCBM(l, w, h, q, u),
              vol_weight: p.volume_weight ? String(p.volume_weight) : calcVolWeight(l, w, h, q, u, div),
            };
          });
          setDimensions(dims.length > 0 ? dims : [{ ...EMPTY_DIMENSION }]);
        }

        // packages
        if (data.packages?.length > 0) {
          setPackages(data.packages.map(p => ({ ...p, _key: p.id || crypto.randomUUID() })));
        }

        // charge groups & logistics commercial quotation items
        if (data.charge_groups?.length > 0) {
          setChargeGroups(data.charge_groups.map(g => ({
            ...g, _key: g.id || crypto.randomUUID(),
            items: (g.items || []).map(i => ({ ...i, _key: i.id || crypto.randomUUID() })),
          })));

          const isLog = (data.shipment_scope === 'DOMESTIC') || (data.mode_of_transport === 'ROAD') || (data.mode_of_transport === 'RAIL');
          if (isLog && data.charge_groups[0]?.items?.length > 0) {
            setLogisticsItems(data.charge_groups[0].items.map(it => ({
              id: it.id,
              _key: it.id || crypto.randomUUID(),
              description: it.description || '',
              rate_type: it.tariff || it.qty_unit || 'Per Trip',
              amount: it.amount !== undefined && it.amount !== null ? String(it.amount) : '',
            })));
          }
        } else {
          setChargeGroups(initChargeGroups(data.mode_of_transport || 'AIR'));
        }

        // containers (FCL)
        if (data.containers?.length > 0) {
          setContainers(data.containers.map(c => ({ ...c, _key: c.id || crypto.randomUUID() })));
        } else {
          setContainers([emptyContainer()]);
        }

        // remarks and logistics metadata from JSON
        if (data.remarks_json) {
          try {
            const parsed = typeof data.remarks_json === 'string' ? JSON.parse(data.remarks_json) : data.remarks_json;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.is_logistics) {
              if (parsed.gst_rate !== undefined) setLogisticsGstRate(String(parsed.gst_rate));
              if (parsed.gst_label) setLogisticsGstLabel(parsed.gst_label);
              if (Array.isArray(parsed.remarks)) {
                setRemarks(parsed.remarks.map((t, i) => ({ _key: String(i), text: typeof t === 'string' ? t : (t.text || '') })));
              }
            } else if (Array.isArray(parsed) && parsed.length > 0) {
              setRemarks(parsed.map((t, i) => ({ _key: String(i), text: typeof t === 'string' ? t : (t.text || '') })));
            }
          } catch {}
        }
      })
      .catch(err => {
        console.error('Failed to load estimate for edit:', err);
        alert(err.message || 'Failed to load estimate');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, initChargeGroups, today, fetchCustomerBranches]);

  // When mode changes, reinitialize charge groups (only on create)
  const handleModeChange = (mode) => {
    setForm(f => ({ ...f, mode_of_transport: mode }));
    if (!isEdit) setChargeGroups(initChargeGroups(mode));
  };

  const schema = getSchema(form.mode_of_transport);

  // Filter locations for Airport / Port dropdowns based on current mode
  const modeLocations = useMemo(() => {
    const isAir = form.mode_of_transport === 'AIR';
    const isSea = form.mode_of_transport === 'SEA';
    if (isAir) {
      return locations.filter(l => l.location_type === 'AIRPORT' || (l.mode_supported && l.mode_supported.includes('AIR')));
    }
    if (isSea) {
      return locations.filter(l => l.location_type === 'SEAPORT' || (l.mode_supported && l.mode_supported.includes('SEA')));
    }
    return locations;
  }, [locations, form.mode_of_transport]);

  // Customer Selection Handlers
  const handleSelectCustomer = (customer) => {
    setIsNewCustomer(false);
    setForm(prev => ({
      ...prev,
      customer_id: customer.id,
      party_name: customer.business_name || '',
      place: customer.city || '',
      attention: customer.contact_person || '',
      is_new_customer: false,
      new_customer_data: null,
      customer_branch_id: '',
    }));
    fetchCustomerBranches(customer.id);
    setIsCustomerPopoverOpen(false);
    setCustomerSearch('');
  };

  const handleStartAddNewCustomer = () => {
    setIsNewCustomer(true);
    setForm(prev => ({
      ...prev,
      customer_id: '',
      customer_branch_id: '',
      is_new_customer: true,
      party_name: newCustomerData.business_name || '',
      place: newCustomerData.city || '',
      attention: newCustomerData.contact_person || '',
    }));
    setIsCustomerPopoverOpen(false);
    setCustomerSearch('');
  };

  const handleNewCustomerFieldChange = (field, value) => {
    const updated = { ...newCustomerData, [field]: value };
    setNewCustomerData(updated);
    setForm(prev => ({
      ...prev,
      new_customer_data: JSON.stringify(updated),
      party_name: field === 'business_name' ? value : prev.party_name,
      place: field === 'city' ? value : prev.place,
      attention: field === 'contact_person' ? value : prev.attention,
    }));
  };

  // Branch Selection Handler
  const handleBranchSelect = (branchId) => {
    const b = customerBranches.find(item => item.id === branchId) || companyBranches.find(item => item.id === branchId);
    setForm(prev => ({
      ...prev,
      customer_branch_id: branchId,
      place: b?.city || prev.place,
      attention: b?.contact_person || prev.attention,
    }));
  };

  // Term / Incoterm change handler
  const handleTermSelectChange = (val) => {
    if (val === 'OTHERS') {
      setForm(prev => ({ ...prev, term: customTerm || 'OTHERS' }));
    } else {
      setCustomTerm('');
      setForm(prev => ({ ...prev, term: val }));
    }
  };

  // ── Dimension Calculator Functions (Reused Docket formulas) ──────────────────
  const isAirMode = form.mode_of_transport === 'AIR';
  const divisor = form.shipment_scope === 'DOMESTIC' ? 5000 : 6000;

  const updateDimensionRow = (idx, field, val) => {
    const updated = dimensions.map((row, i) => {
      if (i !== idx) return row;
      const next = { ...row, [field]: val };
      const l = parseFloat(field === 'length' ? val : row.length) || 0;
      const w = parseFloat(field === 'width' ? val : row.width) || 0;
      const h = parseFloat(field === 'height' ? val : row.height) || 0;
      const q = parseFloat(field === 'no_of_packages' ? val : row.no_of_packages) || 0;

      next.cbm = calcCBM(l, w, h, q, dimensionUnit);
      if (isAirMode) {
        next.vol_weight = calcVolWeight(l, w, h, q, dimensionUnit, divisor);
      }
      return next;
    });
    setDimensions(updated);
  };

  const handleDimensionUnitChange = (newUnit) => {
    setDimensionUnit(newUnit);
    const updated = dimensions.map(row => {
      const l = parseFloat(row.length) || 0;
      const w = parseFloat(row.width) || 0;
      const h = parseFloat(row.height) || 0;
      const q = parseFloat(row.no_of_packages) || 0;
      return {
        ...row,
        cbm: calcCBM(l, w, h, q, newUnit),
        vol_weight: isAirMode ? calcVolWeight(l, w, h, q, newUnit, divisor) : '',
      };
    });
    setDimensions(updated);
  };

  const addDimensionRow = () => {
    setDimensions(prev => [...prev, { ...EMPTY_DIMENSION }]);
  };

  const removeDimensionRow = (idx) => {
    if (dimensions.length <= 1) return;
    setDimensions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddPackageRow = () => {
    setDimensions(prev => [
      ...prev,
      { ...EMPTY_DIMENSION, id: crypto.randomUUID(), unit: dimensionUnit === 'IN' ? 'Inches' : 'CM' }
    ]);
  };

  const handleRemovePackageRow = (idx) => {
    if (dimensions.length <= 1) return;
    setDimensions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpdatePackageRow = (idx, field, val) => {
    setDimensions(prev => {
      const updated = [...prev];
      const cur = { ...updated[idx], [field]: val };
      if (field === 'packages') cur.no_of_packages = val;
      if (field === 'no_of_packages') cur.packages = val;
      const l = parseFloat(field === 'length' ? val : cur.length) || 0;
      const w = parseFloat(field === 'width' ? val : cur.width) || 0;
      const h = parseFloat(field === 'height' ? val : cur.height) || 0;
      const q = parseFloat(cur.packages || cur.no_of_packages) || 0;
      const rUnit = cur.unit || 'CM';
      const normUnit = (rUnit === 'Inches' || rUnit === 'IN') ? 'IN' : 'CM';
      cur.cbm = calcCBM(l, w, h, q, normUnit);
      if (isAirMode) {
        cur.vol_weight = calcVolWeight(l, w, h, q, normUnit, divisor);
      }
      updated[idx] = cur;
      return updated;
    });
  };

  const totalCargoPackages = useMemo(() => {
    return dimensions.reduce((sum, item) => sum + (parseInt(item.packages || item.no_of_packages, 10) || 0), 0);
  }, [dimensions]);

  const totalCargoGrossWeight = useMemo(() => {
    return dimensions.reduce((sum, item) => sum + (parseFloat(item.gross_weight) || 0), 0);
  }, [dimensions]);

  // Computed Totals for Dimensions
  const handleCompanyChange = (newCompId) => {
    setSelectedCompanyId(newCompId);
    const targetComp = companies.find(c => c.id === newCompId);
    if (targetComp) {
      setForm(prev => ({
        ...prev,
        company_id: newCompId,
        signature_text: `Thanking you in advance.\n\nFor, ${targetComp.name}`,
      }));
    }
  };

  const isRoadRailMode = form.mode_of_transport === 'ROAD' || form.mode_of_transport === 'RAIL';

  const dimTotals = useMemo(() => {
    let pkgs = 0;
    let cbmSum = 0;
    let volSum = 0;
    dimensions.forEach(d => {
      const q = parseInt(d.packages || d.no_of_packages, 10) || 0;
      pkgs += q;
      cbmSum += parseFloat(d.cbm) || 0;
      volSum += parseFloat(d.vol_weight) || 0;
    });

    const pkgGrossSum = dimensions.reduce((sum, item) => sum + (parseFloat(item.gross_weight) || 0), 0);
    const gw = (isRoadRailMode && pkgGrossSum > 0) ? pkgGrossSum : (parseFloat(grossWeight) || 0);

    let chWeight = 0;
    if (isAirMode) {
      if (specialCaseCharges && airlineActualWeight) {
        chWeight = parseFloat(airlineActualWeight) || 0;
      } else {
        chWeight = Math.max(gw, volSum);
      }
    } else {
      chWeight = gw || cbmSum;
    }

    return {
      totalPackages: pkgs,
      totalCbm: cbmSum.toFixed(4),
      totalVolWeight: volSum.toFixed(2),
      chargeableWeight: chWeight.toFixed(2),
    };
  }, [dimensions, grossWeight, isAirMode, specialCaseCharges, airlineActualWeight, isRoadRailMode]);

  // Grand Total from all Charge Groups
  const grandTotal = chargeGroups.reduce((sum, g) => sum + (parseFloat(g.group_total) || 0), 0);

  // ── Company Shipment Types & Allowed Modes (from Settings) ───────────────
  const activeCompany = useMemo(() => {
    const targetId = form.company_id || selectedCompanyId;
    return companies.find(c => c.id === targetId) || companies[0] || null;
  }, [companies, form.company_id, selectedCompanyId]);

  const allowedModes = useMemo(() => {
    if (activeCompany) {
      let types = activeCompany.allowed_shipment_types;
      if (typeof types === 'string') {
        try { types = JSON.parse(types); } catch { types = []; }
      }
      if (Array.isArray(types) && types.length > 0) {
        const typesUpper = types.map(t => String(t).toUpperCase());
        const filtered = MODE_OPTIONS.filter(m => typesUpper.includes(m));
        if (filtered.length > 0) return filtered;
      }
    }
    return MODE_OPTIONS;
  }, [activeCompany]);

  // Ensure selected mode is allowed for active company on create
  useEffect(() => {
    if (!isEdit && allowedModes.length > 0) {
      if (!allowedModes.includes(form.mode_of_transport)) {
        handleModeChange(allowedModes[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModes, isEdit, form.mode_of_transport]);

    // ── Domestic Logistics Calculations ─────────────────────────────────────────
  const isLogisticsMode = form.shipment_scope === 'DOMESTIC' || form.mode_of_transport === 'ROAD' || form.mode_of_transport === 'RAIL';

  const logisticsSubTotal = useMemo(() => {
    return logisticsItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  }, [logisticsItems]);

  const logisticsGstAmount = useMemo(() => {
    const rate = parseFloat(logisticsGstRate) || 0;
    return (logisticsSubTotal * rate) / 100;
  }, [logisticsSubTotal, logisticsGstRate]);

  const logisticsTotalPayable = useMemo(() => {
    return logisticsSubTotal + logisticsGstAmount;
  }, [logisticsSubTotal, logisticsGstAmount]);

  // ── Master Vehicle Types & Sizes Computed Options ───────────────────────────
  const vehicleTypeOptions = useMemo(() => {
    if (masterVehicleTypes && masterVehicleTypes.length > 0) {
      return masterVehicleTypes.map(vt => vt.type_name);
    }
    return VEHICLE_TYPE_OPTIONS;
  }, [masterVehicleTypes]);

  const vehicleSizeOptions = useMemo(() => {
    if (masterVehicleTypes && masterVehicleTypes.length > 0) {
      if (form.road_vehicle_type) {
        const matched = masterVehicleTypes.find(
          vt => vt.type_name.toUpperCase() === (form.road_vehicle_type || '').toUpperCase()
        );
        if (matched && matched.sizes && matched.sizes.length > 0) {
          return matched.sizes.map(s => s.size_name);
        }
      }
      const allSizes = masterVehicleTypes.flatMap(vt => (vt.sizes || []).map(s => s.size_name));
      const unique = Array.from(new Set(allSizes.filter(Boolean)));
      if (unique.length > 0) return unique;
    }
    return VEHICLE_SIZE_OPTIONS;
  }, [masterVehicleTypes, form.road_vehicle_type]);

  const updateGroup = (idx, updatedGroup) => {
    const updated = chargeGroups.map((g, i) => i === idx ? updatedGroup : g);
    setChargeGroups(updated);
    setForm(f => ({ ...f, total_amount: updated.reduce((s, g) => s + (parseFloat(g.group_total) || 0), 0) }));
  };

  const addChargeGroup = () => {
    const labels = ['A', 'B', 'C', 'D', 'E', 'F'];
    const nextLabel = labels[chargeGroups.length] || String(chargeGroups.length + 1);
    setChargeGroups(gs => [...gs, { _key: crypto.randomUUID(), group_label: nextLabel, group_name: 'Additional Charges', sort_order: gs.length, group_total: 0, items: [], hasAirline: false, hasTariff: false }]);
  };

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const sel = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && isSuperAdmin && !selectedCompanyId) {
      alert('Please select a target company for this estimate.');
      return;
    }
    if (!form.party_name && !isNewCustomer) {
      alert('Please select or enter Customer Name.');
      return;
    }
    setSaving(true);
    try {
      // Serialize remarks and logistics metadata
      let remarksJson;
      if (isLogisticsMode) {
        remarksJson = JSON.stringify({
          is_logistics: true,
          sub_total: logisticsSubTotal,
          gst_rate: parseFloat(logisticsGstRate) || 0,
          gst_label: logisticsGstLabel,
          gst_amount: logisticsGstAmount,
          total_payable: logisticsTotalPayable,
          remarks: remarks.map(r => r.text).filter(Boolean),
        });
      } else {
        remarksJson = JSON.stringify(remarks.map(r => r.text).filter(Boolean));
      }

      // Serialize charge groups
      let groups;
      if (isLogisticsMode) {
        groups = [
          {
            id: chargeGroups[0]?.id || undefined,
            group_label: 'A',
            group_name: 'Commercial Quotation Breakdown',
            sort_order: 0,
            group_total: logisticsSubTotal,
            items: logisticsItems.map((item, ii) => ({
              id: item.id || undefined,
              description: item.description || '',
              airline_vendor: '',
              tariff: item.rate_type || 'Per Trip',
              amount: parseFloat(item.amount) || 0,
              qty: 1,
              qty_unit: item.rate_type || '',
              total: parseFloat(item.amount) || 0,
              sort_order: ii,
            })),
          }
        ];
      } else {
        groups = chargeGroups.map((g, gi) => ({
          id: g.id || undefined,
          group_label: g.group_label,
          group_name: g.group_name,
          sort_order: gi,
          group_total: g.group_total || 0,
          items: (g.items || []).map((item, ii) => ({
            id: item.id || undefined,
            description: item.description || '',
            airline_vendor: item.airline_vendor || '',
            tariff: item.tariff || '',
            amount: parseFloat(item.amount) || 0,
            qty: parseFloat(item.qty) || 1,
            qty_unit: item.qty_unit || '',
            total: parseFloat(item.total) || 0,
            sort_order: ii,
          })),
        }));
      }

      const totalAmt = isLogisticsMode ? logisticsTotalPayable : grandTotal;

      // Backward compatible packages array
      const effectiveGrossWeight = (isRoadRailMode && totalCargoGrossWeight > 0)
        ? totalCargoGrossWeight
        : (parseFloat(grossWeight) || 0);

      const mappedPackages = dimensions.map((d, i) => ({
        id: packages[i]?.id || undefined,
        package_type: d.package_type || 'Boxes',
        no_of_packages: parseInt(d.packages || d.no_of_packages) || 1,
        dimension: `${d.length || 0}x${d.width || 0}x${d.height || 0}`,
        dimension_unit: (d.unit === 'Inches' || d.unit === 'IN') ? 'IN' : (d.unit || dimensionUnit),
        gross_weight: parseFloat(d.gross_weight) || (isRoadRailMode ? 0 : effectiveGrossWeight),
        volume_weight: parseFloat(d.vol_weight) || 0,
        chargeable_weight: parseFloat(dimTotals.chargeableWeight) || 0,
        weight_unit: 'KGS',
        sort_order: i,
      }));

      const payload = {
        ...form,
        term: (form.shipment_scope === 'DOMESTIC' ? DOMESTIC_TERM_OPTIONS : INCOTERM_OPTIONS).includes(form.term) && form.term !== 'OTHERS'
          ? form.term
          : (customTerm || form.term || (form.shipment_scope === 'DOMESTIC' ? 'PAID' : 'EXW')),
        is_new_customer: isNewCustomer,
        new_customer_data: isNewCustomer ? JSON.stringify(newCustomerData) : null,
        dimension_unit: dimensionUnit,
        special_case_charges: specialCaseCharges,
        airline_actual_weight: parseFloat(airlineActualWeight) || null,
        dimensions_json: JSON.stringify(dimensions),
        gross_weight: effectiveGrossWeight,
        total_cbm: parseFloat(dimTotals.totalCbm) || 0,
        total_volume_weight: parseFloat(dimTotals.totalVolWeight) || 0,
        chargeable_weight: parseFloat(dimTotals.chargeableWeight) || 0,
        remarks_json: remarksJson,
        packages: mappedPackages,
        charge_groups: groups,
        total_amount: totalAmt,
        containers: containers.filter(c => c.container_type).map((c, i) => ({
          id: c.id || undefined,
          container_type: c.container_type,
          general_count: parseInt(c.general_count) || 0,
          reefer_count: parseInt(c.reefer_count) || 0,
          sort_order: i,
        })),
        sea_gross_weight: parseFloat(form.sea_gross_weight) || 0,
        road_rate_per_kg: parseFloat(form.road_rate_per_kg) || 0,
      };

      if (!isEdit && isSuperAdmin && selectedCompanyId) {
        payload.company_id = selectedCompanyId;
      }

      const url = isEdit ? `${API}/api/estimates/${id}` : `${API}/api/estimates`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      navigate('/estimates');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  // Filtered customer list for combobox
  const filteredCustomers = customers.filter(c => {
    if (!customerSearch.trim()) return true;
    const s = customerSearch.toLowerCase();
    return (c.business_name || '').toLowerCase().includes(s) ||
           (c.contact_person || '').toLowerCase().includes(s) ||
           (c.city || '').toLowerCase().includes(s);
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="estimate-form">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/estimates')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-text-dark font-manrope">
              {isEdit ? `Edit Estimate` : 'New Estimate'}
            </h1>
            {form.estimate_no && <p className="text-text-secondary font-ibm text-sm">{form.estimate_no}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={form.status} onValueChange={sel('status')}>
            <SelectTrigger className="w-32 font-ibm text-sm" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {isEdit && (
            <Button type="button" variant="outline" onClick={() => window.open(`/estimates/print/${id}`, '_blank')} className="font-ibm" data-testid="print-estimate-btn">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          )}
          <Button type="submit" disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 font-ibm" data-testid="save-estimate-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Estimate'}
          </Button>
        </div>
      </div>

      {/* ── Super Admin: Company Selector ───────────────────────────────── */}
      {isSuperAdmin && !isEdit && (
        <Card className="bg-amber-50 border border-amber-200 shadow-sm">
          <div className="p-5 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[260px]">
              <h3 className="text-base font-semibold text-text-dark font-manrope flex items-center gap-2">
                Target Company<span className="text-accent-red">*</span>
              </h3>
              <p className="text-xs text-text-secondary font-ibm mt-1">
                As Super Admin, choose which company this estimate belongs to. The estimate number series is unified and common across all companies (e.g. EST-YYYYMM-XXXX).
              </p>
            </div>
            <Select value={selectedCompanyId} onValueChange={handleCompanyChange}>
              <SelectTrigger className="w-80 font-ibm bg-white" data-testid="estimate-company-select">
                <SelectValue placeholder="Select company..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {/* ── Top Classifier ──────────────────────────────────────────────── */}
      <Card className="bg-accent-blue/5 border border-accent-blue/20 shadow-sm">
        <div className="px-6 py-4 border-b border-accent-blue/20">
          <h3 className="text-base font-semibold text-text-dark font-manrope flex items-center gap-2">
            <Calculator className="w-4 h-4 text-accent-blue" />
            Shipment Classification
          </h3>
        </div>
        <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <FormLabel required>International / Domestic</FormLabel>
            <Select value={form.shipment_scope} onValueChange={val => {
              sel('shipment_scope')(val);
              if (val === 'DOMESTIC') {
                if (!isEdit) {
                  setRemarks(DEFAULT_LOGISTICS_REMARKS.map(r => ({ ...r, _key: crypto.randomUUID() })));
                }
                if (!DOMESTIC_TERM_OPTIONS.includes(form.term)) {
                  setForm(prev => ({ ...prev, term: 'PAID' }));
                }
              } else {
                if (!INCOTERM_OPTIONS.includes(form.term)) {
                  setForm(prev => ({ ...prev, term: 'EXW' }));
                }
              }
            }}>
              <SelectTrigger className="font-ibm" data-testid="scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FormLabel required>Mode of Transport</FormLabel>
            <Select value={form.mode_of_transport} onValueChange={val => {
              handleModeChange(val);
              if ((val === 'ROAD' || val === 'RAIL') && !isEdit) {
                setRemarks(DEFAULT_LOGISTICS_REMARKS.map(r => ({ ...r, _key: crypto.randomUUID() })));
              }
            }}>
              <SelectTrigger className="font-ibm" data-testid="mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedModes.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <FormLabel required>Inward / Outward</FormLabel>
            <Select value={form.movement_direction} onValueChange={sel('movement_direction')}>
              <SelectTrigger className="font-ibm" data-testid="direction-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIR_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isLogisticsMode ? (
            <div>
              <FormLabel required>Service Type</FormLabel>
              <div className="pt-0.5">
                <div className="inline-flex p-0.5 bg-gray-100 rounded-lg border border-gray-200 text-xs font-ibm select-none w-full justify-between h-10 items-center">
                  {DOMESTIC_SERVICE_OPTIONS.map(opt => {
                    const isSelected = (form.service || 'General') === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`domestic-service-${opt.toLowerCase()}`}
                        onClick={() => setForm(p => ({ ...p, service: opt }))}
                        className={`flex-1 py-1.5 px-3 rounded-md font-medium text-center transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-sky-600 text-white shadow-xs font-semibold'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <FormLabel>Service</FormLabel>
              <Select value={form.service || '__none__'} onValueChange={v => sel('service')(v === '__none__' ? '' : v)}>
                <SelectTrigger className="font-ibm" data-testid="service-select">
                  <SelectValue placeholder="Select Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select Service</SelectItem>
                  {SERVICE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* FCL / LCL — only shown for SEA */}
          {!isLogisticsMode && form.mode_of_transport === 'SEA' && (
            <div>
              <FormLabel required>FCL / LCL</FormLabel>
              <Select value={form.sea_load_type} onValueChange={sel('sea_load_type')}>
                <SelectTrigger className="font-ibm border-accent-blue/40 bg-blue-50/30" data-testid="sea-load-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LCL">LCL — Less Container Load</SelectItem>
                  <SelectItem value="FCL">FCL — Full Container Load</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* FTL / PTL — shown for domestic logistics or road/rail */}
          {isLogisticsMode && (
            <div>
              <FormLabel required>Load Type (FTL / PTL)</FormLabel>
              <Select value={form.road_load_type} onValueChange={sel('road_load_type')}>
                <SelectTrigger className="font-ibm border-accent-blue/40 bg-blue-50/30" data-testid="road-load-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FTL">FTL — Full Truck Load</SelectItem>
                  <SelectItem value="PTL">PTL — Part Truck Load</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <FormLabel>Cargo / Material Type</FormLabel>
            <Select value={form.cargo_type || '__none__'} onValueChange={v => sel('cargo_type')(v === '__none__' ? '' : v)}>
              <SelectTrigger className="font-ibm" data-testid="cargo-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select</SelectItem>
                {CARGO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* ── Domestic Road Transport Details (FTL / PTL) ────────────────────────────── */}
      {isLogisticsMode && (
        <Card className="bg-blue-50/30 border border-accent-blue/30 shadow-sm">
          <div className="px-6 py-4 border-b border-accent-blue/20">
            <h3 className="text-base font-semibold text-text-dark font-manrope flex items-center gap-2">
              <Calculator className="w-4 h-4 text-accent-blue" />
              Domestic Route &amp; Movement Details ({form.road_load_type || 'FTL'})
            </h3>
          </div>
          <div className="p-6">
            {form.road_load_type === 'PTL' ? (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <FormLabel required>From (Origin City / Area)</FormLabel>
                  <SearchableCombobox
                    value={form.road_from}
                    onChange={val => {
                      setForm(p => ({ ...p, road_from: val, pickup_address: val }));
                    }}
                    options={locations}
                    labelField="displayName"
                    placeholder="Select Origin City / Area..."
                    testId="road-from-ptl-combobox"
                  />
                </div>
                <div>
                  <FormLabel required>To (Destination / Location)</FormLabel>
                  <SearchableCombobox
                    value={form.road_to}
                    onChange={val => {
                      setForm(p => ({ ...p, road_to: val, delivery_at: val }));
                    }}
                    options={locations}
                    labelField="displayName"
                    placeholder="Select Destination / Location..."
                    testId="road-to-ptl-combobox"
                  />
                </div>
                <div>
                  <FormLabel>Delivery Zone</FormLabel>
                  <Select value={form.road_zone || '__none__'} onValueChange={v => sel('road_zone')(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="font-ibm" data-testid="road-zone">
                      <SelectValue placeholder="Select zone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select zone</SelectItem>
                      {ZONE_OPTIONS.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FormLabel>Weight Slab</FormLabel>
                  <Select value={form.road_weight_slab || '__none__'} onValueChange={v => sel('road_weight_slab')(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="font-ibm" data-testid="road-weight-slab">
                      <SelectValue placeholder="Select weight range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select weight range</SelectItem>
                      {WEIGHT_SLAB_OPTIONS.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FormLabel>Rate per KG</FormLabel>
                  <Input type="number" step="0.01" value={form.road_rate_per_kg} onChange={f('road_rate_per_kg')} className="font-ibm" placeholder="0.00" data-testid="road-rate-per-kg" />
                </div>
                <div>
                  <FormLabel>Commodity / Material Description</FormLabel>
                  <Input
                    value={form.commodity || form.road_material_type}
                    onChange={e => {
                      f('commodity')(e);
                      setForm(p => ({ ...p, road_material_type: e.target.value }));
                    }}
                    className="font-ibm"
                    placeholder="e.g. FMCG, Electronics, Machinery"
                    data-testid="road-material-type"
                  />
                </div>
                <div>
                  <FormLabel>TAT (Transit Time)</FormLabel>
                  <div className="flex gap-2">
                    <Input
                      value={tatValue}
                      onChange={e => handleTatChange(e.target.value, tatUnit)}
                      className="font-ibm flex-1"
                      placeholder="e.g. 48 or 2"
                      data-testid="road-tat"
                    />
                    <Select value={tatUnit} onValueChange={u => handleTatChange(tatValue, u)}>
                      <SelectTrigger className="w-28 font-ibm" data-testid="road-tat-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hours">Hours</SelectItem>
                        <SelectItem value="Days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <FormLabel>PO / Reference No</FormLabel>
                  <Input value={form.po_number} onChange={f('po_number')} className="font-ibm" placeholder="PO # / Order Ref" data-testid="po-number" />
                </div>
                <div>
                  <FormLabel>Freight Term</FormLabel>
                  <Select
                    value={DOMESTIC_TERM_OPTIONS.includes(form.term) && form.term !== 'OTHERS' ? form.term : (form.term ? 'OTHERS' : 'PAID')}
                    onValueChange={handleTermSelectChange}
                  >
                    <SelectTrigger className="font-ibm" data-testid="ptl-term-select">
                      <SelectValue placeholder="Select Term" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAID">PAID</SelectItem>
                      <SelectItem value="FOD">FOD (Freight on Delivery)</SelectItem>
                      <SelectItem value="TBB">TBB (To Be Billed)</SelectItem>
                      <SelectItem value="OTHERS">OTHERS</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.term === 'OTHERS' && (
                    <Input
                      value={customTerm}
                      onChange={e => {
                        setCustomTerm(e.target.value);
                        setForm(prev => ({ ...prev, term: e.target.value }));
                      }}
                      placeholder="Enter custom term..."
                      className="font-ibm text-xs mt-1.5"
                    />
                  )}
                </div>
              </div>
            ) : (
              /* FTL Layout */
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
                <div>
                  <FormLabel required>From (Origin City / Area)</FormLabel>
                  <SearchableCombobox
                    value={form.road_from}
                    onChange={val => {
                      setForm(p => ({ ...p, road_from: val, pickup_address: val }));
                    }}
                    options={locations}
                    labelField="displayName"
                    placeholder="Select Origin City / Area..."
                    testId="road-from-ftl-combobox"
                  />
                </div>
                <div>
                  <FormLabel required>To (Destination City / Area)</FormLabel>
                  <SearchableCombobox
                    value={form.road_to}
                    onChange={val => {
                      setForm(p => ({ ...p, road_to: val, delivery_at: val }));
                    }}
                    options={locations}
                    labelField="displayName"
                    placeholder="Select Destination City / Area..."
                    testId="road-to-ftl-combobox"
                  />
                </div>
                <div>
                  <FormLabel>Vehicle Type (From Master)</FormLabel>
                  <Select
                    value={form.road_vehicle_type || '__none__'}
                    onValueChange={v => {
                      const newType = v === '__none__' ? '' : v;
                      setForm(prev => {
                        const matched = masterVehicleTypes.find(vt => vt.type_name.toUpperCase() === newType.toUpperCase());
                        const validSizes = (matched?.sizes || []).map(s => s.size_name.toUpperCase());
                        const shouldKeepSize = validSizes.length === 0 || validSizes.includes((prev.road_vehicle_size || '').toUpperCase());
                        return {
                          ...prev,
                          road_vehicle_type: newType,
                          road_vehicle_size: shouldKeepSize ? prev.road_vehicle_size : '',
                        };
                      });
                    }}
                  >
                    <SelectTrigger className="font-ibm" data-testid="road-vehicle-type">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select vehicle type</SelectItem>
                      {vehicleTypeOptions.map(vt => <SelectItem key={vt} value={vt}>{vt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FormLabel>Vehicle Size (From Master)</FormLabel>
                  <Select
                    value={form.road_vehicle_size || '__none__'}
                    onValueChange={v => sel('road_vehicle_size')(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger className="font-ibm" data-testid="road-vehicle-size">
                      <SelectValue placeholder={form.road_vehicle_type ? `Select size for ${form.road_vehicle_type}` : "Select vehicle size"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select vehicle size</SelectItem>
                      {vehicleSizeOptions.map(vs => <SelectItem key={vs} value={vs}>{vs}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FormLabel>Commodity / Material Description</FormLabel>
                  <Input
                    value={form.commodity || form.road_material_type}
                    onChange={e => {
                      f('commodity')(e);
                      setForm(p => ({ ...p, road_material_type: e.target.value }));
                    }}
                    className="font-ibm"
                    placeholder="e.g. Industrial Goods, Pharma"
                    data-testid="road-material-type"
                  />
                </div>
                <div>
                  <FormLabel>TAT (Transit Time)</FormLabel>
                  <div className="flex gap-2">
                    <Input
                      value={tatValue}
                      onChange={e => handleTatChange(e.target.value, tatUnit)}
                      className="font-ibm flex-1"
                      placeholder="e.g. 48 or 2"
                      data-testid="road-tat"
                    />
                    <Select value={tatUnit} onValueChange={u => handleTatChange(tatValue, u)}>
                      <SelectTrigger className="w-28 font-ibm" data-testid="road-tat-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hours">Hours</SelectItem>
                        <SelectItem value="Days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <FormLabel>PO / Customer Reference No</FormLabel>
                  <Input value={form.po_number} onChange={f('po_number')} className="font-ibm" placeholder="e.g. PO-89212 / Tender # 4" data-testid="po-number" />
                </div>
                <div>
                  <FormLabel>Freight Term</FormLabel>
                  <Select
                    value={DOMESTIC_TERM_OPTIONS.includes(form.term) && form.term !== 'OTHERS' ? form.term : (form.term ? 'OTHERS' : 'PAID')}
                    onValueChange={handleTermSelectChange}
                  >
                    <SelectTrigger className="font-ibm" data-testid="ftl-term-select">
                      <SelectValue placeholder="Select Term" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAID">PAID</SelectItem>
                      <SelectItem value="FOD">FOD (Freight on Delivery)</SelectItem>
                      <SelectItem value="TBB">TBB (To Be Billed)</SelectItem>
                      <SelectItem value="OTHERS">OTHERS</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.term === 'OTHERS' && (
                    <Input
                      value={customTerm}
                      onChange={e => {
                        setCustomTerm(e.target.value);
                        setForm(prev => ({ ...prev, term: e.target.value }));
                      }}
                      placeholder="Enter custom term..."
                      className="font-ibm text-xs mt-1.5"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Basic Estimate Details (Customer & Branch Selection) ──────────── */}
      <SectionCard title="Basic Estimate Details">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <FormLabel required>Estimate Date</FormLabel>
            <Input type="date" value={form.estimate_date} onChange={f('estimate_date')} className="font-ibm" required data-testid="estimate-date" />
          </div>
          <div>
            <FormLabel>Ref No / Estimate No</FormLabel>
            <Input value={form.estimate_no} onChange={f('estimate_no')} className="font-ibm bg-gray-50" placeholder="Auto-generated" data-testid="estimate-no" />
          </div>
          <div>
            <FormLabel required>Currency</FormLabel>
            <Select value={form.currency || 'INR'} onValueChange={sel('currency')}>
              <SelectTrigger className="font-ibm font-semibold" data-testid="estimate-currency-select">
                <SelectValue placeholder="Select Currency" />
              </SelectTrigger>
              <SelectContent className="max-h-64 overflow-y-auto">
                {CURRENCY_OPTIONS.map(c => (
                  <SelectItem key={c} value={c} className="font-ibm font-medium">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 1. To / Customer Name Dropdown with "+ Add New Customer" */}
          <div className="md:col-span-1 lg:col-span-1">
            <FormLabel required>To / Customer Name</FormLabel>
            {isNewCustomer ? (
              <div className="flex items-center gap-1.5">
                <Input
                  value={form.party_name}
                  onChange={e => {
                    handleNewCustomerFieldChange('business_name', e.target.value);
                  }}
                  placeholder="New Customer Name *"
                  className="font-ibm border-amber-300 bg-amber-50/50 font-medium"
                  data-testid="party-name"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsNewCustomer(false);
                    setForm(prev => ({ ...prev, is_new_customer: false, new_customer_data: null }));
                  }}
                  className="text-xs px-2 h-9 text-text-secondary whitespace-nowrap"
                  title="Switch back to existing customer search"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Popover open={isCustomerPopoverOpen} onOpenChange={setIsCustomerPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={isCustomerPopoverOpen}
                    className="w-full justify-between border-gray-200 font-ibm font-normal text-sm h-9 hover:bg-gray-50 px-3 bg-white"
                    data-testid="customer-combobox-trigger"
                  >
                    <span className="truncate text-left font-ibm text-text-dark font-medium">
                      {form.party_name || (form.customer_id && customers.find(c => c.id === form.customer_id)?.business_name) || <span className="text-gray-400 font-normal">Select Customer...</span>}
                    </span>
                    <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-2 bg-white border border-gray-200 shadow-xl rounded-md z-50" align="start">
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                    <Input
                      value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)}
                      placeholder="Search customer..."
                      className="pl-8 h-8 text-xs font-ibm"
                      autoFocus
                    />
                  </div>

                  {/* Add New Customer Option */}
                  <div className="pb-1 mb-1 border-b border-gray-100">
                    <button
                      type="button"
                      onClick={handleStartAddNewCustomer}
                      className="w-full text-left px-2.5 py-1.5 rounded text-xs font-ibm flex items-center gap-2 text-accent-blue hover:bg-blue-50 font-semibold transition-colors"
                      data-testid="add-new-customer-opt"
                    >
                      <UserPlus className="w-3.5 h-3.5 text-accent-blue" />
                      <span>+ Add New Customer</span>
                    </button>
                  </div>

                  {/* Existing Customer List */}
                  <div className="max-h-56 overflow-y-auto space-y-0.5">
                    {filteredCustomers.length === 0 ? (
                      <div className="p-3 text-center text-xs text-gray-400 font-ibm">No customers found</div>
                    ) : (
                      filteredCustomers.map(cust => {
                        const isSelected = form.customer_id === cust.id;
                        return (
                          <button
                            key={cust.id}
                            type="button"
                            onClick={() => handleSelectCustomer(cust)}
                            className={`w-full text-left px-2.5 py-1.5 rounded text-xs font-ibm flex items-center justify-between transition-colors ${
                              isSelected ? 'bg-accent-blue/10 text-accent-blue font-semibold' : 'hover:bg-gray-50 text-text-dark'
                            }`}
                            data-testid={`customer-option-${cust.id}`}
                          >
                            <div className="truncate mr-2">
                              <div className="truncate font-medium">{cust.business_name}</div>
                              <div className="text-[10px] text-gray-400 truncate">
                                {[cust.city, cust.contact_person].filter(Boolean).join(' • ')}
                              </div>
                            </div>
                            {isSelected && <Check className="w-3.5 h-3.5 text-accent-blue flex-shrink-0" />}
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* 2. Branch Dropdown beside Customer Name */}
          <div>
            <FormLabel>Branch</FormLabel>
            {isNewCustomer ? (
              <Input
                value={newCustomerData.branch_nick_name || 'Head Office'}
                onChange={e => handleNewCustomerFieldChange('branch_nick_name', e.target.value)}
                placeholder="Branch / Office"
                className="font-ibm text-xs bg-amber-50/30 border-amber-200"
                data-testid="branch-input"
              />
            ) : (
              <Select
                value={form.customer_branch_id || '__none__'}
                onValueChange={v => handleBranchSelect(v === '__none__' ? '' : v)}
              >
                <SelectTrigger className="font-ibm h-9 text-sm" data-testid="branch-select">
                  <SelectValue placeholder="Select Branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- Default / Main Office --</SelectItem>
                  {/* Customer specific branches if available */}
                  {customerBranches.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.branch_nick_name || b.city || 'Branch'} {b.is_head_office ? '(Head Office)' : ''} {b.city ? `• ${b.city}` : ''}
                    </SelectItem>
                  ))}
                  {/* Company master branches */}
                  {companyBranches.filter(cb => !customerBranches.some(b => b.id === cb.id)).map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.nick_name || b.city || 'Branch'} {b.city ? `• ${b.city}` : ''}
                    </SelectItem>
                  ))}
                  {/* Saved branch fallback if not in lists yet */}
                  {form.customer_branch_id && !customerBranches.some(b => b.id === form.customer_branch_id) && !companyBranches.some(b => b.id === form.customer_branch_id) && (
                    <SelectItem value={form.customer_branch_id}>
                      Selected Branch ({form.customer_branch_id})
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <FormLabel>Place / City</FormLabel>
            <Input
              value={form.place}
              onChange={e => {
                f('place')(e);
                if (isNewCustomer) handleNewCustomerFieldChange('city', e.target.value);
              }}
              className="font-ibm"
              placeholder="e.g. Ahmedabad"
              data-testid="place"
            />
          </div>

          <div>
            <FormLabel>Attention / Contact Person</FormLabel>
            <Input
              value={form.attention}
              onChange={e => {
                f('attention')(e);
                if (isNewCustomer) handleNewCustomerFieldChange('contact_person', e.target.value);
              }}
              className="font-ibm"
              placeholder="Contact Person / Dear Sir"
              data-testid="attention"
            />
          </div>

          <div className="md:col-span-2 lg:col-span-2">
            <FormLabel>Subject</FormLabel>
            <Input value={form.subject} onChange={f('subject')} className="font-ibm" placeholder="Sub: QUOTE || ..." data-testid="subject" />
          </div>

          <div className="md:col-span-2 lg:col-span-4">
            <FormLabel>Intro / Opening Remarks</FormLabel>
            <Textarea value={form.intro_remarks} onChange={f('intro_remarks')} className="font-ibm h-14" placeholder="With ref. to the subject please note below quote." data-testid="intro-remarks" />
          </div>
        </div>

        {/* Inline New Customer Details Section (shown when "+ Add New Customer" is selected) */}
        {isNewCustomer && (
          <div className="mt-4 p-4 rounded-lg bg-amber-50/60 border border-amber-200 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-amber-700" />
                <h4 className="text-sm font-semibold text-amber-900 font-manrope">
                  New Customer Details (Will be added to Customer Master upon approval)
                </h4>
              </div>
              <span className="text-[11px] font-ibm text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded border border-amber-300">
                Temporary details — saved against this estimate
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs font-ibm">
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">Customer Type</Label>
                <select
                  value={newCustomerData.customer_type || 'Company'}
                  onChange={e => handleNewCustomerFieldChange('customer_type', e.target.value)}
                  className="w-full h-8 text-xs font-ibm border border-amber-300 rounded px-2 bg-white mt-1"
                >
                  <option value="Company">Company</option>
                  <option value="Individual">Individual</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">GST Number</Label>
                <Input
                  value={newCustomerData.gst_number || ''}
                  onChange={e => handleNewCustomerFieldChange('gst_number', e.target.value)}
                  placeholder="GSTIN..."
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">Phone / Mobile</Label>
                <Input
                  value={newCustomerData.phone || ''}
                  onChange={e => handleNewCustomerFieldChange('phone', e.target.value)}
                  placeholder="Contact Number"
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">Email</Label>
                <Input
                  value={newCustomerData.email || ''}
                  onChange={e => handleNewCustomerFieldChange('email', e.target.value)}
                  placeholder="name@company.com"
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">Address Line 1</Label>
                <Input
                  value={newCustomerData.address_line_1 || ''}
                  onChange={e => handleNewCustomerFieldChange('address_line_1', e.target.value)}
                  placeholder="Street / Office Address"
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">State</Label>
                <Input
                  value={newCustomerData.state || ''}
                  onChange={e => handleNewCustomerFieldChange('state', e.target.value)}
                  placeholder="State"
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-amber-800 font-medium">Pincode</Label>
                <Input
                  value={newCustomerData.pincode || ''}
                  onChange={e => handleNewCustomerFieldChange('pincode', e.target.value)}
                  placeholder="Pincode"
                  className="h-8 text-xs bg-white border-amber-300 mt-1"
                />
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Shipment Movement Details (Airports / Ports / Delivery At / Incoterm) ──── */}
      {!isLogisticsMode && (
      <SectionCard title="Shipment Movement Details">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <FormLabel>{schema.origin_label}</FormLabel>
            <Textarea value={form.pickup_address} onChange={f('pickup_address')} className="font-ibm h-20" placeholder="Full pickup address" data-testid="pickup-address" />
          </div>

          {/* 4. Loading Field (Searchable Airport/Port based on Mode) */}
          <div>
            <FormLabel>{schema.loading_label || 'Loading Point'}</FormLabel>
            {form.mode_of_transport === 'AIR' || form.mode_of_transport === 'SEA' ? (
              <SearchableCombobox
                value={form.aol}
                onChange={val => setForm(prev => ({ ...prev, aol: val }))}
                options={modeLocations}
                labelField="displayName"
                placeholder={form.mode_of_transport === 'AIR' ? 'Select Airport (AOL)...' : 'Select Port (POL)...'}
                testId="aol-combobox"
              />
            ) : (
              <Input value={form.aol} onChange={f('aol')} className="font-ibm" placeholder="Loading location" data-testid="aol" />
            )}
          </div>

          {/* 4. Discharge Field (Searchable Airport/Port based on Mode) */}
          <div>
            <FormLabel>{schema.discharge_label || 'Discharge Point'}</FormLabel>
            {form.mode_of_transport === 'AIR' || form.mode_of_transport === 'SEA' ? (
              <SearchableCombobox
                value={form.aod}
                onChange={val => setForm(prev => ({ ...prev, aod: val }))}
                options={modeLocations}
                labelField="displayName"
                placeholder={form.mode_of_transport === 'AIR' ? 'Select Airport (AOD)...' : 'Select Port (POD)...'}
                testId="aod-combobox"
              />
            ) : (
              <Input value={form.aod} onChange={f('aod')} className="font-ibm" placeholder="Discharge location" data-testid="aod" />
            )}
          </div>

          {/* 6. Delivery At (Searchable Location Master Dropdown) */}
          <div>
            <FormLabel>{schema.dest_label || 'Delivery At'}</FormLabel>
            <SearchableCombobox
              value={form.delivery_at}
              onChange={val => setForm(prev => ({ ...prev, delivery_at: val }))}
              options={locations}
              labelField="displayName"
              placeholder="Select Delivery Location..."
              testId="delivery-at-combobox"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 3. Term / Incoterm Dropdown with OTHERS custom input */}
          <div>
            <FormLabel>{form.shipment_scope === 'DOMESTIC' ? 'Freight Term' : 'Term / Incoterm'}</FormLabel>
            <div className="space-y-1.5">
              <Select
                value={
                  (form.shipment_scope === 'DOMESTIC' ? DOMESTIC_TERM_OPTIONS : INCOTERM_OPTIONS).includes(form.term) && form.term !== 'OTHERS'
                    ? form.term
                    : (form.term ? 'OTHERS' : (form.shipment_scope === 'DOMESTIC' ? 'PAID' : 'EXW'))
                }
                onValueChange={handleTermSelectChange}
              >
                <SelectTrigger className="font-ibm" data-testid="term-select">
                  <SelectValue placeholder="Select Term" />
                </SelectTrigger>
                <SelectContent>
                  {(form.shipment_scope === 'DOMESTIC' ? DOMESTIC_TERM_OPTIONS : INCOTERM_OPTIONS).map(termOpt => (
                    <SelectItem key={termOpt} value={termOpt}>{termOpt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!(form.shipment_scope === 'DOMESTIC' ? DOMESTIC_TERM_OPTIONS : INCOTERM_OPTIONS).includes(form.term) || form.term === 'OTHERS' || customTerm) && (
                <Input
                  value={customTerm}
                  onChange={e => {
                    setCustomTerm(e.target.value);
                    setForm(prev => ({ ...prev, term: e.target.value }));
                  }}
                  placeholder={form.shipment_scope === 'DOMESTIC' ? "Enter custom term..." : "Enter custom incoterm..."}
                  className="font-ibm text-xs bg-amber-50/30 border-amber-200"
                  data-testid="custom-term-input"
                />
              )}
            </div>
          </div>

          <div>
            <FormLabel>PO No</FormLabel>
            <Input value={form.po_number} onChange={f('po_number')} className="font-ibm" placeholder="PO # ..." data-testid="po-number" />
          </div>
          <div>
            <FormLabel>Commodity / Goods Description</FormLabel>
            <Input value={form.commodity} onChange={f('commodity')} className="font-ibm" placeholder="Description of goods" data-testid="commodity" />
          </div>
          <div className="md:col-span-3">
            <FormLabel>Shipment Notes</FormLabel>
            <Textarea value={form.shipment_notes} onChange={f('shipment_notes')} className="font-ibm h-16" placeholder="Additional shipment notes" data-testid="shipment-notes" />
          </div>
        </div>
      </SectionCard>
      )}

            {/* ── 5. Package / Cargo Details ──── */}
      {form.mode_of_transport === 'SEA' && form.sea_load_type === 'FCL' ? (
        <SectionCard title="Container Details (FCL)">
          <div className="flex items-center gap-4 mb-5">
            <div>
              <FormLabel>Gross Weight (approx)</FormLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={form.sea_gross_weight}
                  onChange={f('sea_gross_weight')}
                  className="font-ibm w-36"
                  placeholder="0.00"
                  data-testid="sea-gross-weight"
                />
                <span className="text-sm text-text-secondary font-ibm">KGS</span>
              </div>
            </div>
          </div>

          {/* Container rows table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Type of Container</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-text-secondary font-ibm">General</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-text-secondary font-ibm">Reefer</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {containers.map((c, idx) => (
                  <tr key={c._key || idx} className="hover:bg-gray-50/60">
                    <td className="px-2 py-1.5 w-64">
                      <Select
                        value={c.container_type || '__none__'}
                        onValueChange={v => setContainers(cs => cs.map((x, i) => i === idx ? { ...x, container_type: v === '__none__' ? '' : v } : x))}
                      >
                        <SelectTrigger className="font-ibm h-8 text-sm" data-testid={`container-type-${idx}`}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Select type</SelectItem>
                          {CONTAINER_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={c.general_count || ''}
                        onChange={e => setContainers(cs => cs.map((x, i) => i === idx ? { ...x, general_count: e.target.value } : x))}
                        className="border-0 bg-transparent h-8 px-1 w-20 font-ibm text-center focus:ring-0"
                        placeholder="0"
                        data-testid={`container-general-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={c.reefer_count || ''}
                        onChange={e => setContainers(cs => cs.map((x, i) => i === idx ? { ...x, reefer_count: e.target.value } : x))}
                        className="border-0 bg-transparent h-8 px-1 w-20 font-ibm text-center focus:ring-0"
                        placeholder="0"
                        data-testid={`container-reefer-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      {containers.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setContainers(cs => cs.filter((_, i) => i !== idx))} className="h-6 w-6 p-0">
                          <Trash2 className="w-3 h-3 text-accent-red" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setContainers(cs => [...cs, emptyContainer()])} className="mt-3 font-ibm text-xs" data-testid="add-container-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Container Row
          </Button>
        </SectionCard>
      ) : isRoadRailMode ? (
        /* ── CARGO PACKAGES & DIMENSIONS (Domestic & International ROAD / RAIL) ── */
        <Card className="bg-white rounded-xl border border-gray-200/90 p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4 text-accent-blue" />
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                Cargo Packages &amp; Dimensions
              </h3>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddPackageRow}
              className="border-dashed border-accent-blue/50 text-accent-blue hover:bg-blue-50/50 text-xs font-ibm font-semibold h-8 cursor-pointer"
              data-testid="add-package-line-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              + Add Package Line
            </Button>
          </div>

          {/* Dynamic Package Rows */}
          <div className="space-y-3">
            {dimensions.map((item, idx) => (
              <div
                key={item.id || item._key || idx}
                className="grid grid-cols-12 gap-3 items-end bg-gray-50/60 p-3 rounded-xl border border-gray-200/80 transition-all"
              >
                {/* Package Type */}
                <div className="col-span-12 sm:col-span-2 space-y-1">
                  <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                    Package Type
                  </Label>
                  <select
                    value={item.package_type || 'Boxes'}
                    onChange={e => handleUpdatePackageRow(idx, 'package_type', e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs font-medium font-ibm shadow-xs focus:ring-2 focus:ring-accent-blue"
                    data-testid={`package-type-${idx}`}
                  >
                    {PACKAGE_TYPE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Packages */}
                <div className="col-span-6 sm:col-span-2 space-y-1">
                  <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                    Packages <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g. 10"
                    value={item.packages || item.no_of_packages || ''}
                    onChange={e => handleUpdatePackageRow(idx, 'packages', e.target.value)}
                    className="h-9 text-xs font-ibm bg-white border-gray-200"
                    data-testid={`package-qty-${idx}`}
                  />
                </div>

                {/* Gross Weight (Kg) */}
                <div className="col-span-6 sm:col-span-2 space-y-1">
                  <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                    Gross Wt (Kg) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 500"
                    value={item.gross_weight || ''}
                    onChange={e => handleUpdatePackageRow(idx, 'gross_weight', e.target.value)}
                    className="h-9 text-xs font-ibm bg-white border-gray-200"
                    data-testid={`package-gross-wt-${idx}`}
                  />
                </div>

                {/* Dimensions (L x W x H) */}
                <div className="col-span-12 sm:col-span-4 space-y-1">
                  <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                    Dimensions (L × W × H)
                  </Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Input
                      type="number"
                      step="any"
                      placeholder="L"
                      value={item.length || ''}
                      onChange={e => handleUpdatePackageRow(idx, 'length', e.target.value)}
                      className="h-9 text-xs font-ibm bg-white border-gray-200"
                      data-testid={`package-len-${idx}`}
                    />
                    <Input
                      type="number"
                      step="any"
                      placeholder="W"
                      value={item.width || ''}
                      onChange={e => handleUpdatePackageRow(idx, 'width', e.target.value)}
                      className="h-9 text-xs font-ibm bg-white border-gray-200"
                      data-testid={`package-width-${idx}`}
                    />
                    <Input
                      type="number"
                      step="any"
                      placeholder="H"
                      value={item.height || ''}
                      onChange={e => handleUpdatePackageRow(idx, 'height', e.target.value)}
                      className="h-9 text-xs font-ibm bg-white border-gray-200"
                      data-testid={`package-height-${idx}`}
                    />
                  </div>
                </div>

                {/* Unit */}
                <div className="col-span-6 sm:col-span-1 space-y-1">
                  <Label className="text-[11px] font-bold text-gray-600 block font-ibm">
                    Unit
                  </Label>
                  <select
                    value={item.unit || 'CM'}
                    onChange={e => handleUpdatePackageRow(idx, 'unit', e.target.value)}
                    className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold font-ibm shadow-xs"
                    data-testid={`package-unit-${idx}`}
                  >
                    <option value="CM">CM</option>
                    <option value="Inches">Inches</option>
                  </select>
                </div>

                {/* Delete action */}
                <div className="col-span-6 sm:col-span-1 flex items-center justify-center pb-1">
                  {dimensions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePackageRow(idx)}
                      className="text-red-500 hover:text-red-700 p-1.5 transition-colors cursor-pointer"
                      title="Remove package line"
                      data-testid={`package-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Total Cargo Summary Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between bg-blue-50/70 border border-blue-100 rounded-xl px-4 py-3 text-xs font-ibm mt-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-blue-900">Total Cargo Summary:</span>
              <span className="text-[11px] text-blue-700">
                ({dimensions.length} {dimensions.length === 1 ? 'line item' : 'line items'})
              </span>
            </div>

            <div className="flex flex-wrap gap-4 sm:gap-6 text-blue-800 font-semibold mt-2 sm:mt-0">
              <span>
                Total Packages: <strong className="text-blue-950 font-bold">{totalCargoPackages}</strong>
              </span>
              <span>
                Total Gross Weight: <strong className="text-blue-950 font-bold">{totalCargoGrossWeight} Kg</strong>
              </span>
              {parseFloat(dimTotals.totalCbm) > 0 && (
                <span>
                  Total CBM: <strong className="text-blue-950 font-bold">{dimTotals.totalCbm} m³</strong>
                </span>
              )}
            </div>
          </div>
        </Card>
      ) : (
        /* AIR, SEA LCL: Existing AIR Docket Dimension Calculator */
        <SectionCard title="Package / Cargo Details — Dimension Calculator">
          <div className="space-y-4">
            {/* Top Toolbar: Add Dimension, Unit Selector & Special Case Charges Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-gray-100">
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addDimensionRow}
                  className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-ibm font-medium"
                  data-testid="add-dimension-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Dimension
                </Button>

                {/* Dimension unit selector */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-text-secondary font-ibm">Unit:</span>
                  <select
                    value={dimensionUnit}
                    onChange={e => handleDimensionUnitChange(e.target.value)}
                    className="h-8 text-xs border border-gray-200 rounded-md px-2 font-ibm bg-white text-text-dark focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    data-testid="dimension-unit-select"
                  >
                    {DIMENSION_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Special Case Charges Toggle (AIR only) */}
              {isAirMode && (
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={specialCaseCharges}
                      onClick={() => {
                        const next = !specialCaseCharges;
                        setSpecialCaseCharges(next);
                        if (next && !airlineActualWeight) {
                          const autoVal = Math.max(parseFloat(grossWeight) || 0, parseFloat(dimTotals.totalVolWeight) || 0);
                          if (autoVal > 0) setAirlineActualWeight(autoVal.toFixed(2));
                        }
                      }}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${specialCaseCharges ? 'bg-amber-500' : 'bg-gray-300'}`}
                      data-testid="special-case-toggle"
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${specialCaseCharges ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <span className="text-xs font-ibm text-text-dark font-medium">Special Case Charges</span>
                    <span className="text-[10px] text-gray-400 font-ibm hidden sm:inline">(airline charges for wasted hold space)</span>
                  </label>

                  {/* Airline Actual Weight Input */}
                  {specialCaseCharges && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-ibm text-amber-800 font-semibold">Airline Actual Weight (kg):</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={airlineActualWeight}
                        onChange={e => setAirlineActualWeight(e.target.value)}
                        placeholder="Enter weight"
                        className="h-8 w-28 text-xs bg-amber-50 text-amber-900 font-semibold border-amber-300"
                        data-testid="airline-actual-weight-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dimension Rows Repeater Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Length ({dimensionUnit})</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Width ({dimensionUnit})</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Height ({dimensionUnit})</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">No of Packages</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">CBM (m³)</th>
                    {isAirMode && (
                      <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">
                        Air Vol. Wt. (kg) <span className="text-[10px] text-gray-400">({form.shipment_scope === 'DOMESTIC' ? '÷5000' : '÷6000'})</span>
                      </th>
                    )}
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dimensions.map((dim, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/60">
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          step="0.01"
                          value={dim.length}
                          onChange={e => updateDimensionRow(idx, 'length', e.target.value)}
                          placeholder="0"
                          className="h-8 text-xs font-ibm"
                          data-testid={`dim-length-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          step="0.01"
                          value={dim.width}
                          onChange={e => updateDimensionRow(idx, 'width', e.target.value)}
                          placeholder="0"
                          className="h-8 text-xs font-ibm"
                          data-testid={`dim-width-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          step="0.01"
                          value={dim.height}
                          onChange={e => updateDimensionRow(idx, 'height', e.target.value)}
                          placeholder="0"
                          className="h-8 text-xs font-ibm"
                          data-testid={`dim-height-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          value={dim.no_of_packages}
                          onChange={e => updateDimensionRow(idx, 'no_of_packages', e.target.value)}
                          placeholder="1"
                          className="h-8 text-xs font-ibm"
                          data-testid={`dim-pkg-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          readOnly
                          value={dim.cbm || ''}
                          placeholder="Auto"
                          className="h-8 text-xs font-ibm bg-emerald-50 text-emerald-800 font-medium cursor-not-allowed border-emerald-200"
                        />
                      </td>
                      {isAirMode && (
                        <td className="px-2 py-1.5">
                          <Input
                            readOnly
                            value={dim.vol_weight || ''}
                            placeholder="Auto"
                            className={`h-8 text-xs font-ibm font-medium cursor-not-allowed border-gray-200 ${
                              form.shipment_scope === 'DOMESTIC' ? 'bg-orange-50 text-orange-800 border-orange-200' : 'bg-blue-50 text-blue-800 border-blue-200'
                            }`}
                          />
                        </td>
                      )}
                      <td className="px-2 py-1.5">
                        {dimensions.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDimensionRow(idx)}
                            className="h-7 w-7 p-0 text-accent-red hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Weights Summary Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 p-4 bg-gray-50/80 rounded-lg border border-gray-200">
              <div>
                <Label className="text-[11px] font-ibm text-text-secondary font-semibold uppercase">Gross Weight (kg) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={grossWeight}
                  onChange={e => setGrossWeight(e.target.value)}
                  placeholder="0.00"
                  className="h-8 text-xs font-ibm bg-white mt-1 border-gray-300 font-semibold"
                  data-testid="gross-weight-input"
                />
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-text-secondary font-semibold uppercase">Total Packages</Label>
                <div className="h-8 flex items-center px-2 text-sm font-bold text-text-dark font-manrope bg-white rounded border border-gray-200 mt-1">
                  {dimTotals.totalPackages || 0}
                </div>
              </div>
              <div>
                <Label className="text-[11px] font-ibm text-text-secondary font-semibold uppercase">Total CBM (m³)</Label>
                <div className="h-8 flex items-center px-2 text-sm font-bold text-emerald-700 font-manrope bg-emerald-50/70 rounded border border-emerald-200 mt-1">
                  {dimTotals.totalCbm || '0.0000'}
                </div>
              </div>
              {isAirMode && (
                <div>
                  <Label className="text-[11px] font-ibm text-text-secondary font-semibold uppercase">Air Vol. Weight (kg)</Label>
                  <div className="h-8 flex items-center px-2 text-sm font-bold text-blue-700 font-manrope bg-blue-50/70 rounded border border-blue-200 mt-1">
                    {dimTotals.totalVolWeight || '0.00'}
                  </div>
                </div>
              )}
              {isAirMode && (
                <div>
                  <Label className="text-[11px] font-ibm text-text-secondary font-semibold uppercase">Chargeable Weight (kg)</Label>
                  <div className="h-8 flex items-center px-2 text-sm font-bold text-purple-700 font-manrope bg-purple-50/70 rounded border border-purple-200 mt-1">
                    {dimTotals.chargeableWeight || '0.00'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {isLogisticsMode ? (
        /* ── Commercial Quotation Breakdown (Logistics Form Matching Screenshot) ── */
        <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-[#1e3a8a] text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-bold tracking-wide font-manrope uppercase flex items-center gap-2">
                <Calculator className="w-5 h-5 text-amber-400" />
                Commercial Quotation Breakdown
              </h3>
              <p className="text-xs text-blue-200 font-ibm mt-0.5">
                Itemized breakdown of transportation, handling, and statutory charges
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                onChange={e => {
                  if (!e.target.value) return;
                  const desc = e.target.value;
                  let defRateType = 'Per Trip';
                  if (desc.includes('Fuel')) defRateType = 'Included';
                  else if (desc.includes('Loading')) defRateType = 'Lump-sum';
                  else if (desc.includes('Toll')) defRateType = 'At Actuals';
                  else if (desc.includes('Detention')) defRateType = 'Per Day';
                  setLogisticsItems(prev => [
                    ...prev,
                    { _key: crypto.randomUUID(), description: desc, rate_type: defRateType, amount: '' }
                  ]);
                  e.target.value = '';
                }}
                className="h-8 text-xs font-ibm bg-white text-gray-800 rounded px-2.5 font-medium border-0 focus:ring-2 focus:ring-amber-400 cursor-pointer shadow-sm"
                defaultValue=""
              >
                <option value="" disabled>+ Add Preset Service / Charge</option>
                {LOGISTICS_CHARGE_PRESETS.map(preset => (
                  <option key={preset} value={preset}>{preset}</option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                onClick={() => setLogisticsItems(prev => [
                  ...prev,
                  { _key: crypto.randomUUID(), description: '', rate_type: 'Per Trip', amount: '' }
                ])}
                className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white font-ibm font-semibold shadow-sm"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Line
              </Button>
            </div>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-[#f8fafc] border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 font-ibm uppercase tracking-wider w-16">
                      Sl. No.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 font-ibm uppercase tracking-wider">
                      Description of Services / Charges
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 font-ibm uppercase tracking-wider w-48">
                      Rate Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 font-ibm uppercase tracking-wider w-48">
                      Amount (INR)
                    </th>
                    <th className="w-12 px-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logisticsItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-xs text-gray-400 font-ibm">
                        No charges added yet. Click &quot;Add Preset Service / Charge&quot; or &quot;Add Custom Line&quot; above.
                      </td>
                    </tr>
                  ) : (
                    logisticsItems.map((item, idx) => (
                      <tr key={item._key || idx} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-2.5 text-center font-ibm font-semibold text-gray-600">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-2.5">
                          <Input
                            value={item.description}
                            onChange={e => {
                              const val = e.target.value;
                              setLogisticsItems(prev => prev.map((x, i) => i === idx ? { ...x, description: val } : x));
                            }}
                            placeholder="e.g. Basic Freight Charges (Fixed Route Route-Trip)"
                            className="font-ibm text-sm font-medium text-gray-900 border-gray-200 focus:border-accent-blue"
                            data-testid={`logistics-item-desc-${idx}`}
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <select
                            value={item.rate_type}
                            onChange={e => {
                              const val = e.target.value;
                              setLogisticsItems(prev => prev.map((x, i) => i === idx ? { ...x, rate_type: val } : x));
                            }}
                            className="w-full h-9 text-xs font-ibm font-semibold border border-gray-200 rounded px-2.5 bg-white text-gray-700 focus:outline-none focus:border-accent-blue text-center"
                            data-testid={`logistics-item-rate-type-${idx}`}
                          >
                            {LOGISTICS_RATE_TYPES.map(rt => (
                              <option key={rt} value={rt}>{rt}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">₹</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.amount}
                              onChange={e => {
                                const val = e.target.value;
                                setLogisticsItems(prev => prev.map((x, i) => i === idx ? { ...x, amount: val } : x));
                              }}
                              placeholder="0.00"
                              className="font-ibm text-sm font-bold text-gray-900 pl-7 text-right border-gray-200 focus:border-accent-blue"
                              data-testid={`logistics-item-amount-${idx}`}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setLogisticsItems(prev => prev.filter((_, i) => i !== idx))}
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom-Right Summary Calculation Box matching screenshot */}
            <div className="mt-6 flex justify-end">
              <div className="w-full sm:w-96 rounded-lg border border-gray-200 bg-gray-50/70 p-4 space-y-3 shadow-sm">
                {/* Sub-Total */}
                <div className="flex items-center justify-between text-sm font-ibm">
                  <span className="font-semibold text-gray-600">Sub-Total:</span>
                  <span className="font-bold text-gray-900 font-manrope">
                    ₹ {logisticsSubTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* GST Selector & Amount */}
                <div className="pt-2 border-t border-gray-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600 font-ibm">GST Rate:</span>
                    <select
                      value={logisticsGstRate}
                      onChange={e => {
                        const val = e.target.value;
                        setLogisticsGstRate(val);
                        if (val === '5') setLogisticsGstLabel('GST (5% GTA RCM/FCM)');
                        else if (val === '12') setLogisticsGstLabel('GST (12% GTA FCM)');
                        else if (val === '18') setLogisticsGstLabel('GST (18% Standard)');
                        else setLogisticsGstLabel('GST (0% Exempt / Nil)');
                      }}
                      className="h-7 text-xs font-ibm font-medium border border-gray-300 rounded px-2 bg-white text-gray-800"
                    >
                      <option value="5">5% GTA (RCM / FCM)</option>
                      <option value="12">12% GTA (Forward Charge FCM)</option>
                      <option value="0">0% (Exempt / Nil)</option>
                      <option value="18">18% (Standard GST)</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between text-sm font-ibm">
                    <span className="font-medium text-gray-600">{logisticsGstLabel}:</span>
                    <span className="font-semibold text-gray-900 font-manrope">
                      ₹ {logisticsGstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Total Estimated Payable */}
                <div className="pt-2.5 border-t-2 border-gray-300 flex items-center justify-between">
                  <span className="text-sm font-bold text-[#1e3a8a] font-ibm uppercase">Total Estimated Payable:</span>
                  <span className="text-lg font-black text-[#1e3a8a] font-manrope" data-testid="logistics-total-payable">
                    ₹ {logisticsTotalPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* ── Charge Groups (Dynamic with Preset Description Dropdowns) ───── */}
      <SectionCard title="Charges">
        {chargeGroups.map((group, idx) => (
          <ChargeGroupTable
            key={group._key || group.id || idx}
            group={group}
            groupIdx={idx}
            schema={schema.defaultGroups?.[idx] || { hasAirline: false, hasTariff: false }}
            onChange={(updated) => updateGroup(idx, updated)}
          />
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addChargeGroup} className="mt-4 font-ibm text-xs" data-testid="add-charge-group-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Charge Group
        </Button>
      </SectionCard>

      {/* ── Summary Total Cost ──────────────────────────────────────────── */}
      <Card className="bg-white border border-gray-100 shadow-sm p-6">
        <h3 className="text-base font-semibold text-text-dark font-manrope mb-4">Summary Total Cost</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-text-secondary font-ibm">Description</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-text-secondary font-ibm">Total</th>
              </tr>
            </thead>
            <tbody>
              {chargeGroups.map((g, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-4 py-2 text-sm font-ibm text-text-dark">{g.group_name} ({g.group_label})</td>
                  <td className="px-4 py-2 text-sm font-semibold text-text-dark font-ibm text-right">
                    {(g.group_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-4 py-3 text-sm font-bold text-text-dark font-ibm uppercase">Grand Total ({form.currency})</td>
                <td className="px-4 py-3 text-base font-bold text-accent-blue font-manrope text-right" data-testid="grand-total">
                  {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
        </>
      )}

      {/* ── Terms & Remarks ─────────────────────────────────────────────── */}
      <SectionCard title="Notes & Remarks" collapsible defaultOpen>
        <div>
          <FormLabel>Highlight Notes</FormLabel>
          <Textarea value={form.highlight_notes} onChange={f('highlight_notes')} className="font-ibm h-24" placeholder="**Important assumptions and conditions..." data-testid="highlight-notes" />
        </div>

        {/* Remarks List */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <FormLabel>Remarks (Numbered List)</FormLabel>
            <Button type="button" variant="outline" size="sm" onClick={() => setRemarks(rs => [...rs, emptyRemark()])} className="font-ibm text-xs" data-testid="add-remark-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Remark
            </Button>
          </div>
          <div className="space-y-2">
            {remarks.map((r, i) => (
              <div key={r._key} className="flex items-center gap-2">
                <span className="text-sm text-text-secondary font-ibm w-5 flex-shrink-0">{i + 1}.</span>
                <Input value={r.text} onChange={e => setRemarks(rs => rs.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} className="font-ibm text-sm flex-1" placeholder="Remark text" data-testid={`remark-${i}`} />
                <Button type="button" variant="ghost" size="sm" onClick={() => setRemarks(rs => rs.filter((_, j) => j !== i))} className="h-7 w-7 p-0">
                  <Trash2 className="w-3 h-3 text-accent-red" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Signature Block */}
        <div className="mt-6">
          <FormLabel>Signature Block</FormLabel>
          <Textarea value={form.signature_text} onChange={f('signature_text')} className="font-ibm h-28" placeholder="Thanking you in advance.&#10;&#10;For, Company Name&#10;&#10;(Authorized Signatory)" data-testid="signature-text" />
        </div>
      </SectionCard>

      {/* Floating Save */}
      <div className="flex justify-end gap-3 pb-8">
        <Button type="button" variant="outline" onClick={() => navigate('/estimates')} className="font-ibm">
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 font-ibm" data-testid="save-estimate-bottom-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving...' : isEdit ? 'Update Estimate' : 'Create Estimate'}
        </Button>
      </div>
    </form>
  );
}
